export interface Rsvp {
  /**
   * ID of the RSVP.
   * @format GUID
   * @readonly
   */
  id?: string | null;
  /**
   * Revision number, which increments by 1 each time the RSVP is updated. To prevent conflicting changes, the existing `revision` must be used when updating the RSVP.
   * @readonly
   */
  revision?: string | null;
  /**
   * Event ID to which the RSVP belongs.
   * @format GUID
   * @immutable
   */
  eventId?: string | null;
  /**
   * Date and time the RSVP was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the RSVP was updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /**
   * Site member ID.
   * @format GUID
   * @immutable
   */
  memberId?: string | null;
  /**
   * Contact ID of a guest who filled in the RSVP form. See the Contacts API for more details.
   * @format GUID
   */
  contactId?: string | null;
  /**
   * First name of a guest who filled in the RSVP form.
   * @minLength 1
   * @maxLength 50
   */
  firstName?: string | null;
  /**
   * Last name of a guest who filled in the RSVP form.
   * @minLength 1
   * @maxLength 50
   */
  lastName?: string | null;
  /**
   * Email of a guest who filled in the RSVP form.
   * @format EMAIL
   * @minLength 4
   * @maxLength 255
   */
  email?: string | null;
  /** Event registration form. */
  form?: FormResponse;
  /** RSVP response status. */
  status?: RsvpStatusWithLiterals;
  /**
   * Total number of guests per 1 RSVP.
   * @readonly
   */
  totalGuests?: number | null;
  /**
   * Guest list.
   * @readonly
   */
  guestList?: GuestList;
  /**
   * 2-letter language code in the [ISO 639-1](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format.
   * @format LANGUAGE
   * @readonly
   */
  language?: string | null;
  /**
   * Locale in the [IETF BCP 47](https://en.wikipedia.org/wiki/IETF_language_tag) format.
   * @format LANGUAGE_TAG
   * @readonly
   */
  locale?: string | null;
  /**
   * Whether all guests are checked in.
   * @readonly
   */
  fullyCheckedIn?: boolean | null;
  /**
   * Number of checked in guests.
   * @readonly
   */
  checkedInGuestCount?: number | null;
  /** Additional guest details. */
  additionalGuestDetails?: AdditionalGuestDetails;
  /** Whether notifications are disabled. */
  disableNotifications?: boolean | null;
  /**
   * Guest login details for accessing the online conference event.
   * @readonly
   */
  onlineConferencingLogin?: OnlineConferencingLogin;
  /**
   * Whether an RSVP is anonymized.
   * @readonly
   */
  anonymized?: boolean | null;
  /** Whether a guest agreed to marketing. */
  marketingConsent?: boolean | null;
  /**
   * Custom field data for the RSVP object.
   *
   * **Note:** You must configure extended fields using schema plugin extensions in your app's dashboard before you can access the extended fields with API calls.
   */
  extendedFields?: ExtendedFields;
}

export interface FormResponse {
  /**
   * Form field inputs.
   * @maxSize 200
   */
  inputValues?: InputValue[];
}

export interface InputValue {
  /**
   * Form field input name.
   * @maxLength 100
   */
  inputName?: string;
  /**
   * Form field value.
   * @maxLength 5000
   */
  value?: string;
  /**
   * Form field values.
   * @maxSize 100
   * @maxLength 5000
   */
  values?: string[];
}

export interface FormattedAddress {
  /**
   * 1-line address representation.
   * @maxLength 200
   */
  formatted?: string;
  /** Address components. */
  address?: CommonAddress;
}

/** Physical address */
export interface CommonAddress extends CommonAddressStreetOneOf {
  /** Street name and number. */
  streetAddress?: CommonStreetAddress;
  /** Main address line, usually street and number as free text. */
  addressLine?: string | null;
  /**
   * Country code.
   * @format COUNTRY
   */
  country?: string | null;
  /** Subdivision shorthand. Usually, a short code (2 or 3 letters) that represents a state, region, prefecture, or province. e.g. NY */
  subdivision?: string | null;
  /** City name. */
  city?: string | null;
  /** Zip/postal code. */
  postalCode?: string | null;
  /** Free text providing more detailed address info. Usually contains Apt, Suite, and Floor. */
  addressLine2?: string | null;
}

/** @oneof */
export interface CommonAddressStreetOneOf {
  /** Street name and number. */
  streetAddress?: CommonStreetAddress;
  /** Main address line, usually street and number as free text. */
  addressLine?: string | null;
}

export interface CommonStreetAddress {
  /** Street number. */
  number?: string;
  /** Street name. */
  name?: string;
}

export interface CommonAddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

export interface CommonSubdivision {
  /** Short subdivision code. */
  code?: string;
  /** Subdivision full name. */
  name?: string;
}

export enum SubdivisionType {
  UNKNOWN_SUBDIVISION_TYPE = 'UNKNOWN_SUBDIVISION_TYPE',
  /** State */
  ADMINISTRATIVE_AREA_LEVEL_1 = 'ADMINISTRATIVE_AREA_LEVEL_1',
  /** County */
  ADMINISTRATIVE_AREA_LEVEL_2 = 'ADMINISTRATIVE_AREA_LEVEL_2',
  /** City/town */
  ADMINISTRATIVE_AREA_LEVEL_3 = 'ADMINISTRATIVE_AREA_LEVEL_3',
  /** Neighborhood/quarter */
  ADMINISTRATIVE_AREA_LEVEL_4 = 'ADMINISTRATIVE_AREA_LEVEL_4',
  /** Street/block */
  ADMINISTRATIVE_AREA_LEVEL_5 = 'ADMINISTRATIVE_AREA_LEVEL_5',
  /** ADMINISTRATIVE_AREA_LEVEL_0. Indicates the national political entity, and is typically the highest order type returned by the Geocoder. */
  COUNTRY = 'COUNTRY',
}

/** @enumType */
export type SubdivisionTypeWithLiterals =
  | SubdivisionType
  | 'UNKNOWN_SUBDIVISION_TYPE'
  | 'ADMINISTRATIVE_AREA_LEVEL_1'
  | 'ADMINISTRATIVE_AREA_LEVEL_2'
  | 'ADMINISTRATIVE_AREA_LEVEL_3'
  | 'ADMINISTRATIVE_AREA_LEVEL_4'
  | 'ADMINISTRATIVE_AREA_LEVEL_5'
  | 'COUNTRY';

/** Subdivision Concordance values */
export interface StandardDetails {
  /**
   * subdivision iso-3166-2 code according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2). e.g. US-NY, GB-SCT, NO-30
   * @maxLength 20
   */
  iso31662?: string | null;
}

export enum RsvpStatus {
  /** Unknown status. This value isn't used. */
  UNKNOWN_RSVP_STATUS = 'UNKNOWN_RSVP_STATUS',
  /** Guest is attending the event. */
  YES = 'YES',
  /** Guest isn't attending the event. */
  NO = 'NO',
  /** Guest is on a waitlist. */
  WAITLIST = 'WAITLIST',
}

/** @enumType */
export type RsvpStatusWithLiterals =
  | RsvpStatus
  | 'UNKNOWN_RSVP_STATUS'
  | 'YES'
  | 'NO'
  | 'WAITLIST';

export interface GuestList {
  /**
   * List of guests.
   * @maxSize 11
   */
  guests?: Guest[];
}

export interface Guest {
  /** Index of each guest in the array. */
  index?: number | null;
  /**
   * Full name.
   * @maxLength 101
   */
  fullName?: string | null;
  /** Check-in details. */
  checkInDetails?: CheckInDetails;
  /**
   * Guest ID.
   * @min 1
   */
  guestId?: number | null;
}

export interface CheckInDetails {
  /** Whether the guest has checked in. */
  checkedIn?: boolean | null;
  /** Date and time the guest checked in. */
  checkInDate?: Date | null;
  /** Date and time the guest cancelled check-in. */
  checkInCanceledDate?: Date | null;
  /**
   * Id of the user who last performed check-in
   * @format GUID
   */
  checkedInBy?: string | null;
  /**
   * Id of the user who last canceled check-in
   * @format GUID
   */
  checkInCanceledBy?: string | null;
  /**
   * Id of the Staff Member who last performed check-in
   * @format GUID
   */
  checkedInByStaffMember?: string | null;
  /**
   * Id of theStaff Member who last canceled check-in
   * @format GUID
   */
  checkInCanceledByStaffMember?: string | null;
}

export interface AdditionalGuestDetails {
  /**
   * Number of additional guests.
   * @max 10
   */
  guestCount?: number | null;
  /**
   * Names of additional guests.
   * @maxSize 10
   * @maxLength 101
   */
  guestNames?: string[] | null;
}

export interface AdditionalGuest {
  /**
   * Guest first name.
   * @maxLength 50
   */
  firstName?: string | null;
  /**
   * Guest last name.
   * @maxLength 50
   */
  lastName?: string | null;
}

export interface OnlineConferencingLogin {
  /**
   * Link URL to the online conference.
   * @format WEB_URL
   * @readonly
   */
  link?: string;
  /**
   * Password for the online conference.
   * @readonly
   */
  password?: string | null;
}

export interface FormSubmission {
  /**
   * Form ID.
   * @readonly
   * @format GUID
   */
  formId?: string | null;
  /**
   * Form submission ID.
   * @readonly
   * @format GUID
   */
  submissionId?: string | null;
  /** Form submissions. */
  submissions?: Record<string, any>;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface InvalidateCache extends InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
  /**
   * tell us why you're invalidating the cache. You don't need to add your app name
   * @maxLength 256
   */
  reason?: string | null;
  /** Is local DS */
  localDc?: boolean;
  hardPurge?: boolean;
  /**
   * Optional caller-provided ID for tracking this invalidation through the system.
   * When set, the corresponding CDN purge completion event will include this ID,
   * allowing you to confirm when the invalidation has fully propagated.
   * Example: generate a UUID, pass it here, and later match it in the CDN purge completion event.
   * @maxLength 256
   */
  correlationId?: string | null;
}

/** @oneof */
export interface InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
}

export interface App {
  /**
   * The AppDefId
   * @minLength 1
   */
  appDefId?: string;
  /**
   * The instance Id
   * @format GUID
   */
  instanceId?: string;
}

export interface Page {
  /**
   * the msid the page is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Page ID
   * @minLength 1
   */
  pageId?: string;
}

export interface URI {
  /**
   * the msid the URI is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI path to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @minLength 1
   */
  uriPath?: string;
}

export interface File {
  /**
   * the msid the file is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by filename (for media files such as PDFs)
   * @minLength 1
   * @maxLength 256
   */
  fileName?: string;
}

export interface CustomTag {
  /**
   * the msid the tag is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Tag to invalidate by
   * @minLength 1
   * @maxLength 256
   */
  tag?: string;
}

export interface Pages {
  /**
   * the msid the pages are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by multiple Page IDs in a single message
   * @maxSize 100
   * @minLength 1
   */
  pageIds?: string[];
}

export interface URIs {
  /**
   * the msid the URIs are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI paths to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @maxSize 100
   * @minLength 1
   */
  uriPaths?: string[];
}

export interface LimitedRsvpSpotsReleased {
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string | null;
  /** Number of spots released. */
  spotsReleased?: number | null;
}

export interface RsvpsBalanceUpdated {
  /** Current event rsvp balance */
  rsvpSummary?: V2RsvpSummary;
  /** Balance change that occurred */
  balanceDelta?: RsvpBalanceDelta;
  /** Event RSVP guest limit */
  guestLimit?: number | null;
}

export interface V2RsvpSummary {
  /**
   * Event ID.
   * @format GUID
   * @readonly
   */
  eventId?: string | null;
  /**
   * Number of RSVPs that answered `yes`.
   * @readonly
   */
  yesGuestCount?: number | null;
  /**
   * Number of RSVPs that answered `no`.
   * @readonly
   */
  noGuestCount?: number | null;
  /**
   * Number of RSVPs that are in the waitlist.
   * @readonly
   */
  waitlistGuestCount?: number | null;
  /**
   * Total number of RSVPs.
   * @readonly
   */
  totalRsvpCount?: number | null;
}

export interface RsvpBalanceDelta {
  /** YES count change */
  yesDelta?: number;
  /** NO count change */
  noDelta?: number;
  /** Waitlist count change */
  waitlistDelta?: number;
  /** RSVP count change */
  totalRsvpDelta?: number;
}

export interface SearchRsvpsRequest {
  /** Search options. See [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language) for more details. */
  search?: CursorSearch;
  /**
   * Predefined sets of fields to return.
   * @maxSize 3
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface CursorSearch extends CursorSearchPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).
   * @maxSize 10
   */
  sort?: Sorting[];
  /**
   * Logical groupings of data into facets, with summaries for each facet.
   * For example, use aggregations to allow site visitors to narrow down their search results by selecting specific categories.
   * @maxSize 10
   */
  aggregations?: Aggregation[];
  /** Free text to match in searchable fields. */
  search?: SearchDetails;
  /**
   * Time zone to adjust date-time-based filters and aggregations, in ISO 8601 (including offsets) or IANA time zone database (including time zone IDs) format.
   * Applies to all relevant filters and aggregations, unless overridden by providing timestamps including time zone. For example, "2023-12-20T10:52:34.795Z".
   * @maxLength 50
   */
  timeZone?: string | null;
}

/** @oneof */
export interface CursorSearchPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  /** Sort is in the ascending order. */
  ASC = 'ASC',
  /** Sort is in the descending order. */
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface Aggregation extends AggregationKindOneOf {
  /** A value aggregation calculates metrics such as count for specific fields within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number (count) of products for each price listed in the store. */
  value?: ValueAggregation;
  /** A range aggregation calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. For example, use a range aggregation to compare the number of reservations made for parties of 4 or less to the number of reservations made for parties with 5 or more. */
  range?: RangeAggregation;
  /** A scalar aggregation calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`. */
  scalar?: ScalarAggregation;
  /** A date histogram calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.) For example, use a date histogram to find how many reservations have been made at a restaurant each week. */
  dateHistogram?: DateHistogramAggregation;
  /** A nested aggregation is applied within the results of another aggregation. Rather than aggregating directly on the primary dataset, first group data using one aggregation and then apply another aggregation within each group. It allows for more complex analyses where you can summarize data at different levels of detail or hierarchy. For example, to get the number of products that are in stock and out of stock for each price listed, first perform a value aggregation on `discountedPriceNumeric`, and a second value aggregation on `inStock`. */
  nested?: NestedAggregation;
  /**
   * Aggregation name displayed in the return.
   * @maxLength 100
   */
  name?: string | null;
  /** Type of aggregation to perform. */
  type?: AggregationTypeWithLiterals;
  /**
   * Field to aggregate by.
   * Use dot notation to specify a JSON path. For example, `order.address.streetName`.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface AggregationKindOneOf {
  /** A value aggregation calculates metrics such as count for specific fields within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number (count) of products for each price listed in the store. */
  value?: ValueAggregation;
  /** A range aggregation calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. For example, use a range aggregation to compare the number of reservations made for parties of 4 or less to the number of reservations made for parties with 5 or more. */
  range?: RangeAggregation;
  /** A scalar aggregation calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`. */
  scalar?: ScalarAggregation;
  /** A date histogram calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.) For example, use a date histogram to find how many reservations have been made at a restaurant each week. */
  dateHistogram?: DateHistogramAggregation;
  /** A nested aggregation is applied within the results of another aggregation. Rather than aggregating directly on the primary dataset, first group data using one aggregation and then apply another aggregation within each group. It allows for more complex analyses where you can summarize data at different levels of detail or hierarchy. For example, to get the number of products that are in stock and out of stock for each price listed, first perform a value aggregation on `discountedPriceNumeric`, and a second value aggregation on `inStock`. */
  nested?: NestedAggregation;
}

export interface RangeBucket {
  /** Inclusive lower bound of the range. Required if `to` is not given. */
  from?: number | null;
  /** Exclusive upper bound of the range. Required if `from` is not given. */
  to?: number | null;
}

export enum SortType {
  /** Number of matches in the results. */
  COUNT = 'COUNT',
  /** Alphabetically by the field value. */
  VALUE = 'VALUE',
}

/** @enumType */
export type SortTypeWithLiterals = SortType | 'COUNT' | 'VALUE';

export enum SortDirection {
  /** Descending order. */
  DESC = 'DESC',
  /** Ascending order. */
  ASC = 'ASC',
}

/** @enumType */
export type SortDirectionWithLiterals = SortDirection | 'DESC' | 'ASC';

export enum MissingValues {
  /** Exclude missing values from the aggregation results. */
  EXCLUDE = 'EXCLUDE',
  /** Include missing values in the aggregation results. */
  INCLUDE = 'INCLUDE',
}

/** @enumType */
export type MissingValuesWithLiterals = MissingValues | 'EXCLUDE' | 'INCLUDE';

export interface IncludeMissingValuesOptions {
  /**
   * Specify a custom name for the bucket containing the missing values.
   * Defaults: `"N/A"` for strings, `0` for integers, and `false` for booleans.
   * @maxLength 20
   */
  addToBucket?: string;
}

export enum ScalarType {
  /** Unknown scalar type. This value is not used. */
  UNKNOWN_SCALAR_TYPE = 'UNKNOWN_SCALAR_TYPE',
  /** Total number of distinct values. */
  COUNT_DISTINCT = 'COUNT_DISTINCT',
  /** Minimum value. */
  MIN = 'MIN',
  /** Maximum value. */
  MAX = 'MAX',
}

/** @enumType */
export type ScalarTypeWithLiterals =
  | ScalarType
  | 'UNKNOWN_SCALAR_TYPE'
  | 'COUNT_DISTINCT'
  | 'MIN'
  | 'MAX';

export interface ValueAggregation extends ValueAggregationOptionsOneOf {
  /**
   * Whether to include or exclude missing values in the aggregation results.
   *
   * Default: `EXCLUDE`.
   */
  includeOptions?: IncludeMissingValuesOptions;
  /** Sort type. */
  sortType?: SortTypeWithLiterals;
  /** Sort direction. */
  sortDirection?: SortDirectionWithLiterals;
  /**
   * Number of aggregation results to return.
   *
   * Min: `1`
   *
   * Max: `250`
   *
   * Default: `10`
   */
  limit?: number | null;
  /** Whether to include or exclude missing values from the aggregation results. <br><br> Default: `EXCLUDE`. */
  missingValues?: MissingValuesWithLiterals;
}

/** @oneof */
export interface ValueAggregationOptionsOneOf {
  /**
   * Whether to include or exclude missing values in the aggregation results.
   *
   * Default: `EXCLUDE`.
   */
  includeOptions?: IncludeMissingValuesOptions;
}

export enum NestedAggregationType {
  /** Unknown aggregation type. This value is not used. */
  UNKNOWN_AGGREGATION_TYPE = 'UNKNOWN_AGGREGATION_TYPE',
  /** Calculates the distribution of a specific field's values within a dataset, providing insights into the overall distribution and key statistics of those values. */
  VALUE = 'VALUE',
  /** Calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. */
  RANGE = 'RANGE',
  /** Calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`. */
  SCALAR = 'SCALAR',
  /** Calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.). */
  DATE_HISTOGRAM = 'DATE_HISTOGRAM',
}

/** @enumType */
export type NestedAggregationTypeWithLiterals =
  | NestedAggregationType
  | 'UNKNOWN_AGGREGATION_TYPE'
  | 'VALUE'
  | 'RANGE'
  | 'SCALAR'
  | 'DATE_HISTOGRAM';

export interface RangeAggregation {
  /**
   * List of range buckets. During aggregation each entity is placed in the first bucket its value falls into, based on the provided range bounds.
   * @maxSize 50
   */
  buckets?: RangeBucket[];
}

export interface ScalarAggregation {
  /** Operator type for the scalar aggregation. */
  type?: ScalarTypeWithLiterals;
}

export interface DateHistogramAggregation {
  /** Interval for date histogram aggregation */
  interval?: IntervalWithLiterals;
}

export enum Interval {
  /** Unknown interval. This value is not used. */
  UNKNOWN_INTERVAL = 'UNKNOWN_INTERVAL',
  YEAR = 'YEAR',
  MONTH = 'MONTH',
  WEEK = 'WEEK',
  DAY = 'DAY',
  HOUR = 'HOUR',
  MINUTE = 'MINUTE',
  SECOND = 'SECOND',
}

/** @enumType */
export type IntervalWithLiterals =
  | Interval
  | 'UNKNOWN_INTERVAL'
  | 'YEAR'
  | 'MONTH'
  | 'WEEK'
  | 'DAY'
  | 'HOUR'
  | 'MINUTE'
  | 'SECOND';

export interface NestedAggregationItem extends NestedAggregationItemKindOneOf {
  /** A value aggregation calculates the distribution of a specific field's values within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number (count) of orders for each order status. */
  value?: ValueAggregation;
  /** A range aggregation calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. For example, use a range aggregation to compare the number of reservations made for parties of 4 or less to the number of reservations made for parties with 5 or more. If ranges overlap, a record that fits more than one range will only be counted in the first range that matches the criteria. */
  range?: RangeAggregation;
  /** A scalar aggregation calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`. */
  scalar?: ScalarAggregation;
  /** A date histogram calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.). For example, use a date histogram to determine how many reservations have been made at a restaurant each week. If ranges overlap, a record that fits more than one range will only be counted in the first range that matches the criteria. */
  dateHistogram?: DateHistogramAggregation;
  /**
   * Unique, caller-defined aggregation name, returned in `aggregations.results`.
   * @maxLength 100
   */
  name?: string | null;
  /** Type of aggregation to perform. The matching aggregation field must be passed. */
  type?: NestedAggregationTypeWithLiterals;
  /**
   * Field to aggregate by.
   * Use dot notation to specify a JSON path. For example, `order.address.streetName`.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface NestedAggregationItemKindOneOf {
  /** A value aggregation calculates the distribution of a specific field's values within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number (count) of orders for each order status. */
  value?: ValueAggregation;
  /** A range aggregation calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. For example, use a range aggregation to compare the number of reservations made for parties of 4 or less to the number of reservations made for parties with 5 or more. If ranges overlap, a record that fits more than one range will only be counted in the first range that matches the criteria. */
  range?: RangeAggregation;
  /** A scalar aggregation calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`. */
  scalar?: ScalarAggregation;
  /** A date histogram calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.). For example, use a date histogram to determine how many reservations have been made at a restaurant each week. If ranges overlap, a record that fits more than one range will only be counted in the first range that matches the criteria. */
  dateHistogram?: DateHistogramAggregation;
}

export enum AggregationType {
  /** Unknown type. This value is not used. */
  UNKNOWN_AGGREGATION_TYPE = 'UNKNOWN_AGGREGATION_TYPE',
  /** Calculates the distribution of a specific field's values within a dataset, providing insights into the overall distribution and key statistics of those values. */
  VALUE = 'VALUE',
  /** Calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. */
  RANGE = 'RANGE',
  /** Calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`. */
  SCALAR = 'SCALAR',
  /** Calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.). */
  DATE_HISTOGRAM = 'DATE_HISTOGRAM',
  /** Flattened list of aggregations, where each aggregation is nested within previous one. */
  NESTED = 'NESTED',
}

/** @enumType */
export type AggregationTypeWithLiterals =
  | AggregationType
  | 'UNKNOWN_AGGREGATION_TYPE'
  | 'VALUE'
  | 'RANGE'
  | 'SCALAR'
  | 'DATE_HISTOGRAM'
  | 'NESTED';

/** Flattened list of aggregations, where each aggregation is nested within previous one. */
export interface NestedAggregation {
  /**
   * Flattened list of aggregations, where each aggregation is nested within previous one.
   * @minSize 2
   * @maxSize 3
   */
  nestedAggregations?: NestedAggregationItem[];
}

export interface SearchDetails {
  /** Search mode. Defines the search logic for combining multiple terms in the `expression`. */
  mode?: ModeWithLiterals;
  /**
   * Search term or expression.
   * @maxLength 100
   */
  expression?: string | null;
  /**
   * Fields to search in. If the array is empty, all searchable fields are searched.
   * Use dot notation to specify a JSON path. For example, For example, `order.address.streetName`.
   * @maxLength 200
   * @maxSize 20
   */
  fields?: string[];
  /** Whether to enable the search function to use an algorithm to automatically find results that are close to the search expression, such as typos and declensions. */
  fuzzy?: boolean;
}

export enum Mode {
  /** At least one of the search terms must be present. */
  OR = 'OR',
  /** Searches by all provided words. */
  AND = 'AND',
}

/** @enumType */
export type ModeWithLiterals = Mode | 'OR' | 'AND';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export enum RequestedFields {
  /** Unknown requested field. This value isn't used. */
  UNKNOWN_REQUESTED_FIELD = 'UNKNOWN_REQUESTED_FIELD',
  /** Returns `createdDate`, `updatedDate`, `firstName`, `lastName`, `totalGuests`, `guests`. */
  DETAILS = 'DETAILS',
  /** Returns `form`. */
  FORM = 'FORM',
  /** Returns `email`. */
  CONTACT_DETAILS = 'CONTACT_DETAILS',
}

/** @enumType */
export type RequestedFieldsWithLiterals =
  | RequestedFields
  | 'UNKNOWN_REQUESTED_FIELD'
  | 'DETAILS'
  | 'FORM'
  | 'CONTACT_DETAILS';

export interface SearchRsvpsResponse {
  /** Retrieved RSVPs. */
  rsvps?: Rsvp[];
  /** Metadata for the paginated results. */
  pagingMetadata?: CursorPagingMetadata;
  /** Aggregation data. */
  aggregationData?: AggregationData;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface AggregationData {
  /**
   * List of the aggregated data results.
   * @maxSize 10000
   */
  results?: AggregationResults[];
}

export interface ValueAggregationResult {
  /**
   * Value of the field.
   * @maxLength 100
   */
  value?: string;
  /** Total number of entities with this value. */
  count?: number;
}

export interface RangeAggregationResult {
  /** Inclusive lower bound of the range. */
  from?: number | null;
  /** Exclusive upper bound of the range. */
  to?: number | null;
  /** Count of entities in this range. */
  count?: number;
}

export interface NestedAggregationResults
  extends NestedAggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Range aggregation results. */
  ranges?: RangeResults;
  /** Scalar aggregation results. */
  scalar?: AggregationResultsScalarResult;
  /**
   * Unique, caller-defined aggregation name, identifiable by the requested aggregation `name`.
   * @maxLength 100
   */
  name?: string;
  /** Aggregation type. */
  type?: AggregationTypeWithLiterals;
  /**
   * Field which the data was aggregated by.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface NestedAggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Range aggregation results. */
  ranges?: RangeResults;
  /** Scalar aggregation results. */
  scalar?: AggregationResultsScalarResult;
}

export interface ValueResults {
  /**
   * List of ranges returned in the same order as requested.
   * @maxSize 250
   */
  results?: ValueAggregationResult[];
}

export interface RangeResults {
  /**
   * List of ranges returned in same order as requested.
   * @maxSize 50
   */
  results?: RangeAggregationResult[];
}

export interface AggregationResultsScalarResult {
  /** Type of scalar aggregation. */
  type?: ScalarTypeWithLiterals;
  /** Value of the scalar aggregation. */
  value?: number;
}

export interface NestedValueAggregationResult {
  /**
   * Value of the field.
   * @maxLength 1000
   */
  value?: string;
  /** Nested aggregations. */
  nestedResults?: NestedAggregationResults;
}

export interface ValueResult {
  /**
   * Value of the field.
   * @maxLength 1000
   */
  value?: string;
  /** Total number of entities with this value. */
  count?: number | null;
}

export interface RangeResult {
  /** Inclusive lower bound of the range. */
  from?: number | null;
  /** Exclusive upper bound of the range. */
  to?: number | null;
  /** Total number of entities in this range. */
  count?: number | null;
}

export interface ScalarResult {
  /** Value of the scalar aggregation. */
  value?: number;
}

export interface NestedResultValue extends NestedResultValueResultOneOf {
  /** Value aggregation result. */
  value?: ValueResult;
  /** Range aggregation result. */
  range?: RangeResult;
  /** Scalar aggregation result. */
  scalar?: ScalarResult;
  /** Date histogram aggregation result. */
  dateHistogram?: ValueResult;
}

/** @oneof */
export interface NestedResultValueResultOneOf {
  /** Value aggregation result. */
  value?: ValueResult;
  /** Range aggregation result. */
  range?: RangeResult;
  /** Scalar aggregation result. */
  scalar?: ScalarResult;
  /** Date histogram aggregation result. */
  dateHistogram?: ValueResult;
}

export interface Results {
  /** List of nested aggregations. */
  results?: Record<string, NestedResultValue>;
}

export interface DateHistogramResult {
  /**
   * Date in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format.
   * @maxLength 100
   */
  value?: string;
  /** Total number of entities in the bucket. */
  count?: number;
}

export interface GroupByValueResults {
  /**
   * List of value aggregations.
   * @maxSize 1000
   */
  results?: NestedValueAggregationResult[];
}

export interface DateHistogramResults {
  /**
   * List of date histogram aggregations.
   * @maxSize 200
   */
  results?: DateHistogramResult[];
}

/** Results of `NESTED` aggregation type in a flattened array, identifiable by the requested aggregation `name`. */
export interface NestedResults {
  /**
   * List of nested aggregations.
   * @maxSize 1000
   */
  results?: Results[];
}

export interface AggregationResults extends AggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Range aggregation results. */
  ranges?: RangeResults;
  /** Scalar aggregation results. */
  scalar?: AggregationResultsScalarResult;
  /** Group by value aggregation results. */
  groupedByValue?: GroupByValueResults;
  /** Date histogram aggregation results. */
  dateHistogram?: DateHistogramResults;
  /** Nested aggregation results. */
  nested?: NestedResults;
  /**
   * Aggregation name displayed in the return.
   * @maxLength 100
   */
  name?: string;
  /** Type of aggregation that was performed. */
  type?: AggregationTypeWithLiterals;
  /**
   * Field which the data was aggregated by.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface AggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Range aggregation results. */
  ranges?: RangeResults;
  /** Scalar aggregation results. */
  scalar?: AggregationResultsScalarResult;
  /** Group by value aggregation results. */
  groupedByValue?: GroupByValueResults;
  /** Date histogram aggregation results. */
  dateHistogram?: DateHistogramResults;
  /** Nested aggregation results. */
  nested?: NestedResults;
}

export interface SearchRsvpsWithOffsetRequest {
  /** Search options. See [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language) for more details. */
  search?: Search;
  /**
   * Predefined sets of fields to return.
   * @maxSize 3
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface Search extends SearchPagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not filter or sort. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).
   * @maxSize 10
   */
  sort?: Sorting[];
  /**
   * Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned.
   * @maxLength 200
   * @maxSize 20
   */
  fields?: string[];
  /**
   * Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned.
   * @maxLength 100
   * @maxSize 20
   */
  fieldsets?: string[];
  /**
   * Logical groupings of data into facets, with summaries for each facet.
   * For example, use aggregations to allow site visitors to narrow down their search results by selecting specific categories.
   * @maxSize 10
   */
  aggregations?: Aggregation[];
  /** Free text to match in searchable fields. */
  search?: SearchDetails;
  /**
   * Time zone to adjust date-time-based filters and aggregations, in ISO 8601 (including offsets) or IANA time zone database (including time zone IDs) format.
   * Applies to all relevant filters and aggregations, unless overridden by providing timestamps including time zone. For example, "2023-12-20T10:52:34.795Z".
   * @maxLength 50
   */
  timeZone?: string | null;
}

/** @oneof */
export interface SearchPagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not filter or sort. */
  cursorPaging?: CursorPaging;
}

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface SearchRsvpsWithOffsetResponse {
  /** Retrieved RSVPs. */
  rsvps?: Rsvp[];
  /** Metadata for the paginated results. */
  pagingMetadata?: PagingMetadataV2;
  /** Aggregation data. */
  aggregationData?: AggregationData;
}

export interface PagingMetadataV2 {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
  /** Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used. */
  cursors?: Cursors;
}

export interface ListRsvpsRequest {
  /**
   * ID of the RSVP to retrieve.
   * @format GUID
   */
  eventId?: string;
  cursorPaging?: LargeCursorPaging;
  /**
   * Predefined sets of fields to return.
   * @maxSize 3
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface LargeCursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 1000
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface ListRsvpsResponse {
  /** Retrieved RSVPs. */
  rsvps?: Rsvp[];
  /** Metadata for the paginated results. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface QueryRsvpsRequest {
  /** Query options. See [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language) for more details. */
  query?: CursorQuery;
  /**
   * Predefined sets of fields to return.
   * @maxSize 3
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface QueryRsvpsResponse {
  /** Retrieved RSVPs. */
  rsvps?: Rsvp[];
  /** Metadata for the paginated results. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CountRsvpsRequest {
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /** Search details. */
  search?: SearchDetails;
}

export interface CountRsvpsResponse {
  /** Number of RSVPs. */
  count?: number;
}

export interface GetRsvpRequest {
  /**
   * ID of the RSVP to retrieve.
   * @format GUID
   */
  rsvpId: string;
  /**
   * Predefined sets of fields to return.
   * @maxSize 3
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface GetRsvpResponse {
  /** Retrieved RSVP. */
  rsvp?: Rsvp;
}

export interface CreateRsvpRequest {
  /** RSVP info. */
  rsvp: Rsvp;
  /** RSVP options. <br> The **WIX_EVENTS.MANAGE_RSVP** permission is required. */
  options?: ModificationOptions;
  /**
   * Predefined sets of fields to return.
   * @maxSize 3
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface ModificationOptions {
  /** Whether to create or update an RSVP regardless of the event's guest limit. */
  ignoreLimits?: boolean;
  /** Whether to ignore the form validation. */
  ignoreFormValidation?: boolean;
  /** Whether to ignore the response status. */
  ignoreResponseStatus?: boolean;
  /** Whether to ignore the additional guest names validation. */
  ignoreAdditionalGuestNames?: boolean;
}

export interface CreateRsvpResponse {
  /** Created RSVP. */
  rsvp?: Rsvp;
  /** "Add to calendar" links. */
  calendarLinks?: CalendarLinks;
}

export interface CalendarLinks {
  /** "Add to Google calendar" URL. */
  google?: string;
  /** "Download ICS calendar file" URL. */
  ics?: string;
}

export interface GuestLimitExceededData {
  /** Maximum size of RSVP guest list. */
  maxRsvpSize?: number;
}

export interface UpdateRsvpRequest {
  /** RSVP to update. */
  rsvp: Rsvp;
  /** RSVP options. <br> The **WIX_EVENTS.MANAGE_RSVP** permission is required to pass these fields. */
  options?: ModificationOptions;
  /**
   * Predefined sets of fields to return.
   * @maxSize 3
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface UpdateRsvpResponse {
  /** Updated RSVP. */
  rsvp?: Rsvp;
}

export interface DeleteRsvpRequest {
  /**
   * ID of the RSVP to delete.
   * @format GUID
   */
  rsvpId: string | null;
}

export interface DeleteRsvpResponse {}

export interface BulkUpdateRsvpsRequest {
  /**
   * RSVPs to update.
   * @minSize 1
   * @maxSize 100
   */
  rsvps: MaskedRsvp[];
  /** Whether to return the whole `rsvp` object in the response. If `false`, only metadata is returned. */
  returnEntity?: boolean;
  /**
   * Predefined sets of fields to return.
   * @maxSize 3
   */
  fields?: RequestedFieldsWithLiterals[];
  /** RSVP options. <br> The **WIX_EVENTS.MANAGE_RSVP** permission is required to pass these fields. */
  options?: ModificationOptions;
}

export interface MaskedRsvp {
  /** RSVP to update. */
  rsvp?: Rsvp;
  /** Explicit list of fields to update. */
  fieldMask?: string[];
}

export interface BulkUpdateRsvpsResponse {
  /**
   * Response of bulk update.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkRsvpResult[];
  /** Response metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @format GUID
   */
  id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkRsvpResult {
  /** Item metadata. */
  itemMetadata?: ItemMetadata;
  /** Updated RSVP. */
  rsvp?: Rsvp;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkDeleteRsvpsByFilterRequest {
  /** Filter object. */
  filter: Record<string, any> | null;
  /** Search details. */
  search?: SearchDetails;
}

export interface BulkDeleteRsvpsByFilterResponse {
  /**
   * Job ID. Call [Get Async Job](https://dev.wix.com/docs/rest/business-management/async-job/get-async-job) with this ID to retrieve the job details and metadata.
   * @format GUID
   */
  jobId?: string;
}

export interface CheckInRsvpGuestsRequest {
  /**
   * ID of the RSVP to check-in.
   * @format GUID
   */
  rsvpId: string | null;
  /**
   * IDs of guests to check-in.
   * @min 1
   * @minSize 1
   * @maxSize 11
   */
  guestIds?: number[];
  /**
   * Predefined sets of fields to return.
   * @maxSize 3
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface CheckInRsvpGuestsResponse {
  /** Updated RSVP. */
  rsvp?: Rsvp;
}

export interface CancelRsvpGuestsCheckInRequest {
  /**
   * ID of the RSVP to cancel check-in for.
   * @format GUID
   */
  rsvpId: string;
  /**
   * IDs of guests to cancel check-in for.
   * @min 1
   * @minSize 1
   * @maxSize 11
   */
  guestIds?: number[];
  /**
   * Predefined sets of fields to return.
   * @maxSize 3
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface CancelRsvpGuestsCheckInResponse {
  /** Updated RSVP. */
  rsvp?: Rsvp;
}

export interface ListRsvpSummaryRequest {
  /**
   * Event ID to retrieve the RSVP summary for.
   * @format GUID
   * @minSize 1
   * @maxSize 100
   */
  eventId: string[] | null;
}

export interface ListRsvpSummaryResponse {
  /** Retrieved RSVP summary. */
  summaries?: V2RsvpSummary[];
}

export interface ListCurrentRsvpsRequest {
  /**
   * Event IDs to retrieve member RSVPs for.
   * @format GUID
   * @minSize 1
   * @maxSize 25
   */
  eventId: string[] | null;
  /**
   * Predefined sets of fields to return.
   * @maxSize 3
   */
  fields?: RequestedFieldsWithLiterals[];
  /** Paging options. */
  paging?: CursorPaging;
}

export interface ListCurrentRsvpsResponse {
  /** Retrieved RSVPs for the member. */
  rsvps?: Rsvp[];
  /** Metadata for the paginated results. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface EventCreated {
  /** Event created timestamp in ISO UTC format. */
  timestamp?: Date | null;
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string;
  /** Event location. */
  location?: Location;
  /** Event schedule configuration. */
  scheduleConfig?: ScheduleConfig;
  /** Event title. */
  title?: string;
  /**
   * Event creator user ID.
   * @maxLength 36
   */
  userId?: string | null;
  /** Event status. */
  status?: EventStatusWithLiterals;
  /**
   * Instance ID. Indicates the original app instance which current event was derived from.
   * @format GUID
   */
  derivedFromInstanceId?: string | null;
  /**
   * Event ID. Indicates the original event which current event was derived from.
   * @format GUID
   */
  derivedFromEventId?: string | null;
  /** Event that was created. */
  event?: Event;
}

export interface Location {
  /**
   * Location name.
   * @maxLength 50
   */
  name?: string | null;
  /** Location map coordinates. */
  coordinates?: MapCoordinates;
  /**
   * Single line address representation.
   * @maxLength 300
   */
  address?: string | null;
  /** Location type. */
  type?: LocationTypeWithLiterals;
  /**
   * Full address derived from formatted single line `address`.
   * When `full_address` is used to create or update the event, deprecated `address` and `coordinates` are ignored.
   * If provided `full_address` has empty `formatted_address` or `coordinates`, it will be auto-completed using Atlas service.
   *
   * Migration notes:
   * - `full_address.formatted_address` is equivalent to `address`.
   * - `full_address.geocode` is equivalent to `coordinates`.
   */
  fullAddress?: Address;
  /**
   * Defines event location as TBD (To Be Determined).
   * When event location is not yet defined, `name` is displayed instead of location address.
   * `coordinates`, `address`, `type` and `full_address` are not required when location is TBD.
   */
  tbd?: boolean | null;
}

export interface MapCoordinates {
  /**
   * Latitude.
   * @min -90
   * @max 90
   */
  lat?: number;
  /**
   * Longitude.
   * @min -180
   * @max 180
   */
  lng?: number;
}

export enum LocationType {
  VENUE = 'VENUE',
  ONLINE = 'ONLINE',
}

/** @enumType */
export type LocationTypeWithLiterals = LocationType | 'VENUE' | 'ONLINE';

/** Physical address */
export interface Address extends AddressStreetOneOf {
  /** a break down of the street to number and street name */
  streetAddress?: StreetAddress;
  /** Main address line (usually street and number) as free text */
  addressLine?: string | null;
  /**
   * country code
   * @format COUNTRY
   */
  country?: string | null;
  /** subdivision (usually state or region) code according to ISO 3166-2 */
  subdivision?: string | null;
  /** city name */
  city?: string | null;
  /** zip/postal code */
  postalCode?: string | null;
  /** Free text providing more detailed address info. Usually contains Apt, Suite, Floor */
  addressLine2?: string | null;
  /** A string containing the human-readable address of this location */
  formattedAddress?: string | null;
  /** Free text for human-to-human textual orientation aid purposes */
  hint?: string | null;
  /** coordinates of the physical address */
  geocode?: AddressLocation;
  /** country full-name */
  countryFullname?: string | null;
  /**
   * multi-level subdivisions from top to bottom
   * @maxSize 6
   */
  subdivisions?: Subdivision[];
}

/** @oneof */
export interface AddressStreetOneOf {
  /** a break down of the street to number and street name */
  streetAddress?: StreetAddress;
  /** Main address line (usually street and number) as free text */
  addressLine?: string | null;
}

export interface StreetAddress {
  /** street number */
  number?: string;
  /** street name */
  name?: string;
}

export interface AddressLocation {
  /**
   * address latitude coordinates
   * @min -90
   * @max 90
   */
  latitude?: number | null;
  /**
   * address longitude coordinates
   * @min -180
   * @max 180
   */
  longitude?: number | null;
}

export interface Subdivision {
  /** subdivision short code */
  code?: string;
  /** subdivision full-name */
  name?: string;
}

export interface ScheduleConfig {
  /**
   * Defines event as TBD (To Be Determined) schedule.
   * When event time is not yet defined, TBD message is displayed instead of event start and end times.
   * `startDate`, `endDate` and `timeZoneId` are not required when schedule is TBD.
   */
  scheduleTbd?: boolean;
  /**
   * TBD message.
   * @maxLength 100
   */
  scheduleTbdMessage?: string | null;
  /** Event start timestamp. */
  startDate?: Date | null;
  /** Event end timestamp. */
  endDate?: Date | null;
  /**
   * Event time zone ID in TZ database format, e.g., `EST`, `America/Los_Angeles`.
   * @maxLength 100
   */
  timeZoneId?: string | null;
  /** Whether end date is hidden in the formatted schedule. */
  endDateHidden?: boolean;
  /** Whether time zone is displayed in formatted schedule. */
  showTimeZone?: boolean;
  /** Event recurrences. */
  recurrences?: Recurrences;
}

export interface Recurrences {
  /**
   * Event occurrences.
   * @maxSize 1000
   */
  occurrences?: Occurrence[];
  /**
   * Recurring event category ID.
   * @readonly
   */
  categoryId?: string | null;
  /**
   * Recurrence status.
   * @readonly
   */
  status?: StatusWithLiterals;
}

export interface Occurrence {
  /** Event start timestamp. */
  startDate?: Date | null;
  /** Event end timestamp. */
  endDate?: Date | null;
  /**
   * Event time zone ID in TZ database format, e.g., `EST`, `America/Los_Angeles`.
   * @maxLength 100
   */
  timeZoneId?: string | null;
  /** Whether time zone is displayed in formatted schedule. */
  showTimeZone?: boolean;
}

export enum Status {
  /** Event occurs only once. */
  ONE_TIME = 'ONE_TIME',
  /** Event is recurring. */
  RECURRING = 'RECURRING',
  /** Marks the next upcoming occurrence of the recurring event. */
  RECURRING_NEXT = 'RECURRING_NEXT',
  /** Marks the most recent ended occurrence of the recurring event. */
  RECURRING_LAST_ENDED = 'RECURRING_LAST_ENDED',
  /** Marks the most recent canceled occurrence of the recurring event. */
  RECURRING_LAST_CANCELED = 'RECURRING_LAST_CANCELED',
}

/** @enumType */
export type StatusWithLiterals =
  | Status
  | 'ONE_TIME'
  | 'RECURRING'
  | 'RECURRING_NEXT'
  | 'RECURRING_LAST_ENDED'
  | 'RECURRING_LAST_CANCELED';

export enum EventStatus {
  /** Event is public and scheduled to start */
  SCHEDULED = 'SCHEDULED',
  /** Event has started */
  STARTED = 'STARTED',
  /** Event has ended */
  ENDED = 'ENDED',
  /** Event was canceled */
  CANCELED = 'CANCELED',
}

/** @enumType */
export type EventStatusWithLiterals =
  | EventStatus
  | 'SCHEDULED'
  | 'STARTED'
  | 'ENDED'
  | 'CANCELED';

export interface Event {
  /**
   * Event ID.
   * @format GUID
   * @readonly
   */
  id?: string;
  /** Event location. */
  location?: Location;
  /** Event scheduling. */
  scheduling?: Scheduling;
  /** Event title. */
  title?: string;
  /** Event description. */
  description?: string;
  /** Rich-text content that are displayed in a site's "About Event" section (HTML). */
  about?: string;
  /** Main event image. */
  mainImage?: Image;
  /** Event slug URL (generated from event title). */
  slug?: string;
  /** ISO 639-1 language code of the event (used in content translations). */
  language?: string;
  /** Event creation timestamp. */
  created?: Date | null;
  /** Event modified timestamp. */
  modified?: Date | null;
  /** Event status. */
  status?: EventStatusWithLiterals;
  /** RSVP or ticketing registration details. */
  registration?: Registration;
  /** "Add to calendar" URLs. */
  calendarLinks?: CalendarLinks;
  /** Event page URL components. */
  eventPageUrl?: SiteUrl;
  /** Event registration form. */
  form?: Form;
  /** Event dashboard summary of RSVP / ticket sales. */
  dashboard?: Dashboard;
  /** Instance ID of the site where event is hosted. */
  instanceId?: string;
  /** Guest list configuration. */
  guestListConfig?: GuestListConfig;
  /**
   * Event creator user ID.
   * @maxLength 36
   */
  userId?: string;
  /** Event discussion feed. For internal use. */
  feed?: Feed;
  /** Online conferencing details. */
  onlineConferencing?: OnlineConferencing;
  /** SEO settings. */
  seoSettings?: SeoSettings;
  /** Assigned contacts label key. */
  assignedContactsLabel?: string | null;
  /** Agenda details. */
  agenda?: Agenda;
  /** Categories this event is assigned to. */
  categories?: Category[];
  /** Visual settings for event. */
  eventDisplaySettings?: EventDisplaySettings;
  /** Rich content that are displayed in a site's "About Event" section. Successor to `about` field. */
  longDescription?: RichContent;
  /**
   * Event publish timestamp.
   * @readonly
   */
  publishedDate?: Date | null;
}

export interface Scheduling {
  /** Schedule configuration. */
  config?: ScheduleConfig;
  /** Formatted schedule representation. */
  formatted?: string;
  /** Formatted start date of the event (empty for TBD schedules). */
  startDateFormatted?: string;
  /** Formatted start time of the event (empty for TBD schedules). */
  startTimeFormatted?: string;
  /** Formatted end date of the event (empty for TBD schedules or when end date is hidden). */
  endDateFormatted?: string;
  /** Formatted end time of the event (empty for TBD schedules or when end date is hidden). */
  endTimeFormatted?: string;
}

export interface Image {
  /**
   * WixMedia image ID.
   * @minLength 1
   * @maxLength 200
   */
  id?: string | null;
  /** Image URL. */
  url?: string;
  /** Original image height. */
  height?: number | null;
  /** Original image width. */
  width?: number | null;
  /** Image alt text. Optional. */
  altText?: string | null;
}

export interface Registration {
  /** Event type. */
  type?: EventTypeWithLiterals;
  /** Event registration status. */
  status?: RegistrationStatusWithLiterals;
  /** RSVP collection details. */
  rsvpCollection?: RsvpCollection;
  /** Ticketing details. */
  ticketing?: Ticketing;
  /** External registration details. */
  external?: ExternalEvent;
  /** Types of users allowed to register. */
  restrictedTo?: VisitorTypeWithLiterals;
  /** Initial event type which was set when creating an event. */
  initialType?: EventTypeWithLiterals;
}

export enum EventType {
  /** Type not available for this request fieldset */
  NA_EVENT_TYPE = 'NA_EVENT_TYPE',
  /** Registration via RSVP */
  RSVP = 'RSVP',
  /** Registration via ticket purchase */
  TICKETS = 'TICKETS',
  /** External registration */
  EXTERNAL = 'EXTERNAL',
  /** Registration not available */
  NO_REGISTRATION = 'NO_REGISTRATION',
}

/** @enumType */
export type EventTypeWithLiterals =
  | EventType
  | 'NA_EVENT_TYPE'
  | 'RSVP'
  | 'TICKETS'
  | 'EXTERNAL'
  | 'NO_REGISTRATION';

export enum RegistrationStatus {
  /** Registration status is not applicable */
  NA_REGISTRATION_STATUS = 'NA_REGISTRATION_STATUS',
  /** Registration to event is closed */
  CLOSED = 'CLOSED',
  /** Registration to event is closed manually */
  CLOSED_MANUALLY = 'CLOSED_MANUALLY',
  /** Registration is open via RSVP */
  OPEN_RSVP = 'OPEN_RSVP',
  /** Registration to event waitlist is open via RSVP */
  OPEN_RSVP_WAITLIST = 'OPEN_RSVP_WAITLIST',
  /** Registration is open via ticket purchase */
  OPEN_TICKETS = 'OPEN_TICKETS',
  /** Registration is open via external URL */
  OPEN_EXTERNAL = 'OPEN_EXTERNAL',
  /** Registration will be open via RSVP */
  SCHEDULED_RSVP = 'SCHEDULED_RSVP',
}

/** @enumType */
export type RegistrationStatusWithLiterals =
  | RegistrationStatus
  | 'NA_REGISTRATION_STATUS'
  | 'CLOSED'
  | 'CLOSED_MANUALLY'
  | 'OPEN_RSVP'
  | 'OPEN_RSVP_WAITLIST'
  | 'OPEN_TICKETS'
  | 'OPEN_EXTERNAL'
  | 'SCHEDULED_RSVP';

export interface RsvpCollection {
  /** RSVP collection configuration. */
  config?: RsvpCollectionConfig;
}

export interface RsvpCollectionConfig {
  /** Defines the supported RSVP statuses. */
  rsvpStatusOptions?: RsvpStatusOptionsWithLiterals;
  /**
   * Total guest limit available to register to the event.
   * Additional guests per RSVP are counted towards total guests.
   */
  limit?: number | null;
  /** Whether a waitlist is opened when total guest limit is reached, allowing guests to create RSVP with WAITING RSVP status. */
  waitlist?: boolean;
  /** Registration start timestamp. */
  startDate?: Date | null;
  /** Registration end timestamp. */
  endDate?: Date | null;
}

export enum RsvpStatusOptions {
  /** Only YES RSVP status is available for RSVP registration */
  YES_ONLY = 'YES_ONLY',
  /** YES and NO RSVP status options are available for the registration */
  YES_AND_NO = 'YES_AND_NO',
}

/** @enumType */
export type RsvpStatusOptionsWithLiterals =
  | RsvpStatusOptions
  | 'YES_ONLY'
  | 'YES_AND_NO';

export interface RsvpConfirmationMessages {
  /** Messages displayed when an RSVP's `status` is set to `"YES"`. */
  positiveConfirmation?: RsvpConfirmationMessagesPositiveResponseConfirmation;
  /** Messages displayed when an RSVP's `status` is set to `"WAITLIST"`, for when the event is full and a waitlist is available). */
  waitlistMessages?: RsvpConfirmationMessagesPositiveResponseConfirmation;
  /** Messages displayed when an RSVP's `status` is set to `"NO"`. */
  negativeMessages?: RsvpConfirmationMessagesNegativeResponseConfirmation;
}

/** Confirmation shown after then registration when RSVP response is positive. */
export interface RsvpConfirmationMessagesPositiveResponseConfirmation {
  /**
   * Confirmation message title.
   * @maxLength 150
   */
  title?: string | null;
  /**
   * Confirmation message text.
   * @maxLength 350
   */
  message?: string | null;
  /**
   * "Add to calendar" call-to-action label text.
   * @maxLength 50
   */
  addToCalendarActionLabel?: string | null;
  /**
   * "Share event" call-to-action label text.
   * @maxLength 50
   */
  shareActionLabel?: string | null;
}

/** Confirmation shown after then registration when RSVP response is negative. */
export interface RsvpConfirmationMessagesNegativeResponseConfirmation {
  /**
   * Confirmation message title.
   * @maxLength 150
   */
  title?: string | null;
  /**
   * "Share event" call-to-action label text.
   * @maxLength 50
   */
  shareActionLabel?: string | null;
}

export interface Ticketing {
  /**
   * Deprecated.
   * @deprecated
   */
  lowestPrice?: string | null;
  /**
   * Deprecated.
   * @deprecated
   */
  highestPrice?: string | null;
  /** Currency used in event transactions. */
  currency?: string | null;
  /** Ticketing configuration. */
  config?: TicketingConfig;
  /**
   * Price of lowest priced ticket.
   * @readonly
   */
  lowestTicketPrice?: Money;
  /**
   * Price of highest priced ticket.
   * @readonly
   */
  highestTicketPrice?: Money;
  /**
   * Formatted price of lowest priced ticket.
   * @readonly
   */
  lowestTicketPriceFormatted?: string | null;
  /**
   * Formatted price of highest priced ticket.
   * @readonly
   */
  highestTicketPriceFormatted?: string | null;
  /**
   * Whether all tickets are sold for this event.
   * @readonly
   */
  soldOut?: boolean | null;
}

export interface TicketingConfig {
  /** Whether the form must be filled out separately for each ticket. */
  guestAssignedTickets?: boolean;
  /** Tax configuration. */
  taxConfig?: TaxConfig;
  /**
   * Limit of tickets that can be purchased per order, default 20.
   * @max 50
   */
  ticketLimitPerOrder?: number;
  /**
   * Duration for which the tickets being bought are reserved.
   * @min 5
   * @max 30
   */
  reservationDurationInMinutes?: number | null;
}

export interface TaxConfig {
  /** Tax application settings. */
  type?: TaxTypeWithLiterals;
  /**
   * Tax name.
   * @minLength 1
   * @maxLength 10
   */
  name?: string | null;
  /**
   * Tax rate (e.g.,`21.55`).
   * @decimalValue options { gte:0.001, lte:100, maxScale:3 }
   */
  rate?: string | null;
  /** Applies taxes for donations, default true. */
  appliesToDonations?: boolean | null;
}

export enum TaxType {
  /** Tax is included in the ticket price. */
  INCLUDED = 'INCLUDED',
  /** Tax is added to the order at the checkout. */
  ADDED = 'ADDED',
  /** Tax is added to the final total at the checkout. */
  ADDED_AT_CHECKOUT = 'ADDED_AT_CHECKOUT',
}

/** @enumType */
export type TaxTypeWithLiterals =
  | TaxType
  | 'INCLUDED'
  | 'ADDED'
  | 'ADDED_AT_CHECKOUT';

export interface TicketsConfirmationMessages {
  /**
   * Confirmation message title.
   * @maxLength 150
   */
  title?: string | null;
  /**
   * Confirmation message text.
   * @maxLength 350
   */
  message?: string | null;
  /**
   * "Download tickets" call-to-action label text.
   * @maxLength 50
   */
  downloadTicketsLabel?: string | null;
  /**
   * "Add to calendar" call-to-action label text.
   * @maxLength 50
   */
  addToCalendarActionLabel?: string | null;
  /**
   * "Share event" call-to-action label text.
   * @maxLength 50
   */
  shareActionLabel?: string | null;
}

export enum CheckoutType {
  UNKNOWN_CHECKOUT_TYPE = 'UNKNOWN_CHECKOUT_TYPE',
  /** Checkout using Events App. */
  EVENTS_APP = 'EVENTS_APP',
  /** Checkout using Ecomm Platform. */
  ECOMM_PLATFORM = 'ECOMM_PLATFORM',
}

/** @enumType */
export type CheckoutTypeWithLiterals =
  | CheckoutType
  | 'UNKNOWN_CHECKOUT_TYPE'
  | 'EVENTS_APP'
  | 'ECOMM_PLATFORM';

export interface Money {
  /**
   * *Deprecated:** Use `value` instead.
   * @format DECIMAL_VALUE
   * @deprecated
   */
  amount?: string;
  /**
   * 3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.
   * @format CURRENCY
   */
  currency?: string;
  /**
   * Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.
   * @format DECIMAL_VALUE
   */
  value?: string | null;
}

export interface ExternalEvent {
  /** External event registration URL. */
  registration?: string;
}

export enum VisitorType {
  /** Site visitor (including member) */
  VISITOR = 'VISITOR',
  /** Site member */
  MEMBER = 'MEMBER',
  /** Site visitor or member */
  VISITOR_OR_MEMBER = 'VISITOR_OR_MEMBER',
}

/** @enumType */
export type VisitorTypeWithLiterals =
  | VisitorType
  | 'VISITOR'
  | 'MEMBER'
  | 'VISITOR_OR_MEMBER';

/** Site URL components */
export interface SiteUrl {
  /**
   * Base URL. For premium sites, this will be the domain.
   * For free sites, this would be site URL (e.g `mysite.wixsite.com/mysite`)
   */
  base?: string;
  /** The path to that page - e.g `/my-events/weekly-meetup-2` */
  path?: string;
}

/**
 * The form defines which elements are displayed to a site visitor during the registration process (RSVP or checkout).
 * It also contains customizable messages and labels.
 *
 *
 * A form is an ordered list of controls (blocks), which accept guest information into a field input.
 *
 * Each control contains one or more nested inputs. For example, `Name` control has two inputs:
 * - First Name
 * - Last Name
 *
 * By default, name and email controls are always required and are pinned to the top of the form.
 */
export interface Form {
  /** Nested fields as an ordered list. */
  controls?: InputControl[];
  /**
   * Set of defined form messages displayed in the UI before, during, and after a registration flow.
   * Includes the configuration of form titles, response labels, "thank you" messages, and call-to-action texts.
   */
  messages?: FormMessages;
}

/**
 * A block of nested fields.
 * Used to aggregate similar inputs like First Name and Last Name.
 */
export interface InputControl {
  /** Field control type. */
  type?: InputControlTypeWithLiterals;
  /** Whether the control is mandatory (such as `name` & `email`). When `true`, only the label can be changed. */
  system?: boolean;
  /**
   * Deprecated: Use `id` or `_id`.
   * @deprecated
   */
  name?: string;
  /** Child inputs. */
  inputs?: Input[];
  /**
   * *Deprecated:** Use `controls.inputs.label`.
   * @deprecated
   */
  label?: string;
  /** Field controls are sorted by this value in ascending order. */
  orderIndex?: number;
  /** Unique control ID. */
  id?: string;
  /**
   * Whether the input control is deleted.
   * @readonly
   */
  deleted?: boolean | null;
}

export enum InputControlType {
  /** Single text value field. */
  INPUT = 'INPUT',
  /** Single text value field with multiple lines. */
  TEXTAREA = 'TEXTAREA',
  /** Single-choice field with predefined values. */
  DROPDOWN = 'DROPDOWN',
  /** Single-choice field with predefined values. */
  RADIO = 'RADIO',
  /** Multiple-choice field with predefined values. */
  CHECKBOX = 'CHECKBOX',
  /** Fields for entering first and last names. */
  NAME = 'NAME',
  /** Fields for additional guests and their respective names. */
  GUEST_CONTROL = 'GUEST_CONTROL',
  /** Single-line address field. */
  ADDRESS_SHORT = 'ADDRESS_SHORT',
  /** Full address field with multiple lines. */
  ADDRESS_FULL = 'ADDRESS_FULL',
  /** Fields for entering year, month, and day. */
  DATE = 'DATE',
}

/** @enumType */
export type InputControlTypeWithLiterals =
  | InputControlType
  | 'INPUT'
  | 'TEXTAREA'
  | 'DROPDOWN'
  | 'RADIO'
  | 'CHECKBOX'
  | 'NAME'
  | 'GUEST_CONTROL'
  | 'ADDRESS_SHORT'
  | 'ADDRESS_FULL'
  | 'DATE';

/** Child inputs. */
export interface Input {
  /** Field name. */
  name?: string;
  /**
   * *Deprecated:** Use `controls.inputs.type.TEXT_ARRAY`.
   * @deprecated
   */
  array?: boolean;
  /** Main field label. */
  label?: string;
  /** Additional labels for multi-valued fields such as address. */
  additionalLabels?: Record<string, string>;
  /** Predefined choice options for fields, such as dropdown. */
  options?: string[];
  /** Whether field is mandatory. */
  mandatory?: boolean;
  /** Maximum number of accepted characters (relevant for text fields). */
  maxLength?: number;
  /**
   * Type which determines field format.
   * Used to validate submitted response.
   */
  type?: ValueTypeWithLiterals;
  /**
   * The maximum number of accepted values for array input.
   *
   * **Note:** Only applicable for `TEXT_ARRAY` input fields.
   */
  maxSize?: number | null;
  /**
   * Default option initially selected when an input has multiple choices.
   *
   * Defaults to first (0th) option, if not configured.
   * Currently only applicable for `type.dropdown`.
   */
  defaultOptionSelection?: OptionSelection;
  /**
   * Additional labels for multi-valued fields, such as address.
   * @readonly
   */
  labels?: Label[];
}

export enum ValueType {
  TEXT = 'TEXT',
  NUMBER = 'NUMBER',
  TEXT_ARRAY = 'TEXT_ARRAY',
  DATE_TIME = 'DATE_TIME',
  ADDRESS = 'ADDRESS',
}

/** @enumType */
export type ValueTypeWithLiterals =
  | ValueType
  | 'TEXT'
  | 'NUMBER'
  | 'TEXT_ARRAY'
  | 'DATE_TIME'
  | 'ADDRESS';

/**
 * Describes initially selected option when an input has multiple choices.
 * Defaults to first (0th) option if not configured.
 */
export interface OptionSelection extends OptionSelectionSelectedOptionOneOf {
  /**
   * 0-based index from predefined `controls.inputs.options` which is initial selection.
   * @max 199
   */
  optionIndex?: number;
  /**
   * Placeholder hint describing expected choices, such as "Please select".
   * Considered an empty choice.
   * @maxLength 200
   */
  placeholderText?: string;
}

/** @oneof */
export interface OptionSelectionSelectedOptionOneOf {
  /**
   * 0-based index from predefined `controls.inputs.options` which is initial selection.
   * @max 199
   */
  optionIndex?: number;
  /**
   * Placeholder hint describing expected choices, such as "Please select".
   * Considered an empty choice.
   * @maxLength 200
   */
  placeholderText?: string;
}

export interface Label {
  /** Field name. */
  name?: string;
  /** Field label. */
  label?: string;
}

/**
 * Defines form messages shown in UI before, during, and after registration flow.
 * It enables configuration of form titles, response labels, "thank you" messages, and call-to-action texts.
 */
export interface FormMessages {
  /** [RSVP form](https://dev.wix.com/docs/rest/business-solutions/events/rsvp-v2/introduction) messages. */
  rsvp?: RsvpFormMessages;
  /** Checkout form messages. */
  checkout?: CheckoutFormMessages;
  /** Messages shown when event registration is closed. */
  registrationClosed?: RegistrationClosedMessages;
  /** Messages shown when event tickets are unavailable. */
  ticketsUnavailable?: TicketsUnavailableMessages;
}

export interface RsvpFormMessages {
  /** Label text indicating RSVP's `status` is `"YES"`. */
  rsvpYesOption?: string;
  /** Label text indicating RSVP's `status` is `"NO"`. */
  rsvpNoOption?: string;
  /** Messages displayed when an RSVP's `status` is set to `"YES"`. */
  positiveMessages?: Positive;
  /** Messages displayed when an RSVP's `status` is set to `"WAITLIST"`, for when the event is full and a waitlist is available). */
  waitlistMessages?: Positive;
  /** Messages displayed when an RSVP's `status` is set to `"NO"`. */
  negativeMessages?: Negative;
  /** "Submit form" call-to-action label text. */
  submitActionLabel?: string;
}

/** Confirmation messages shown after registration. */
export interface PositiveResponseConfirmation {
  /** Confirmation message title. */
  title?: string;
  /** Confirmation message text. */
  message?: string;
  /** "Add to calendar" call-to-action label text. */
  addToCalendarActionLabel?: string;
  /** "Share event" call-to-action label text. */
  shareActionLabel?: string;
}

/** Confirmation messages shown after registration. */
export interface NegativeResponseConfirmation {
  /** Confirmation message title. */
  title?: string;
  /** "Share event" call-to-action label text. */
  shareActionLabel?: string;
}

/** Set of messages shown during registration when RSVP response is positive. */
export interface Positive {
  /** Main form title for positive response. */
  title?: string;
  /** Confirmation messages shown after registration. */
  confirmation?: PositiveResponseConfirmation;
}

/** A set of messages shown during registration with negative response */
export interface Negative {
  /** Main form title for negative response. */
  title?: string;
  /** Confirmation messages shown after registration. */
  confirmation?: NegativeResponseConfirmation;
}

export interface CheckoutFormMessages {
  /** Main form title for response. */
  title?: string;
  /** Submit form call-to-action label text. */
  submitActionLabel?: string;
  /** Confirmation messages shown after checkout. */
  confirmation?: ResponseConfirmation;
}

/** Confirmation messages shown after checkout. */
export interface ResponseConfirmation {
  /** Confirmation message title. */
  title?: string;
  /** Confirmation message text. */
  message?: string;
  /** "Download tickets" call-to-action label text. */
  downloadTicketsLabel?: string;
  /** "Add to calendar" call-to-action label text. */
  addToCalendarLabel?: string;
  /** "Share event" call-to-action label text. */
  shareEventLabel?: string;
}

export interface RegistrationClosedMessages {
  /** Message shown when event registration is closed. */
  message?: string;
  /** "Explore other events" call-to-action label text. */
  exploreEventsActionLabel?: string;
}

export interface TicketsUnavailableMessages {
  /** Message shown when event tickets are unavailable. */
  message?: string;
  /** "Explore other events" call-to-action label text. */
  exploreEventsActionLabel?: string;
}

export interface Dashboard {
  /** Guest RSVP summary. */
  rsvpSummary?: RsvpSummary;
  /**
   * Summary of revenue and tickets sold.
   * (Archived orders are not included).
   */
  ticketingSummary?: TicketingSummary;
}

export interface RsvpSummary {
  /** Total number of RSVPs. */
  total?: number;
  /** Number of RSVPs with status `YES`. */
  yes?: number;
  /** Number of RSVPs with status `NO`. */
  no?: number;
  /** Number of RSVPs in waitlist. */
  waitlist?: number;
}

export interface TicketingSummary {
  /** Number of tickets sold. */
  tickets?: number;
  /**
   * Total revenue, excluding fees.
   * (taxes and payment provider fees are not deducted.)
   */
  revenue?: Money;
  /** Whether currency is locked and cannot be changed (generally occurs after the first order in the specified currency has been created). */
  currencyLocked?: boolean;
  /** Number of orders placed. */
  orders?: number;
  /** Total balance of confirmed transactions. */
  totalSales?: Money;
}

export interface GuestListConfig {
  /** Whether members can see other members attending the event (defaults to true). */
  publicGuestList?: boolean;
}

export interface Feed {
  /** Event discussion feed token. */
  token?: string;
}

export interface OnlineConferencing {
  config?: OnlineConferencingConfig;
  session?: OnlineConferencingSession;
}

export interface OnlineConferencingConfig {
  /**
   * Whether online conferencing is enabled (not supported for TBD schedules).
   * When enabled, links to join conferencing are generated and provided to guests.
   */
  enabled?: boolean;
  /**
   * Conferencing provider ID.
   * @format GUID
   */
  providerId?: string | null;
  /** Conference type */
  conferenceType?: ConferenceTypeWithLiterals;
}

export enum ConferenceType {
  /** Everyone in the meeting can publish and subscribe video and audio. */
  MEETING = 'MEETING',
  /** Guests can only subscribe to video and audio. */
  WEBINAR = 'WEBINAR',
}

/** @enumType */
export type ConferenceTypeWithLiterals = ConferenceType | 'MEETING' | 'WEBINAR';

export interface OnlineConferencingSession {
  /**
   * Link for event host to start the online conference session.
   * @readonly
   */
  hostLink?: string;
  /**
   * Link for guests to join the online conference session.
   * @readonly
   */
  guestLink?: string;
  /**
   * The password required to join online conferencing session (when relevant).
   * @readonly
   */
  password?: string | null;
  /**
   * Indicates that session was created successfully on providers side.
   * @readonly
   */
  sessionCreated?: boolean | null;
  /**
   * Unique session id
   * @readonly
   */
  sessionId?: string | null;
}

export interface SeoSettings {
  /**
   * URL slug
   * @maxLength 130
   */
  slug?: string;
  /** Advanced SEO data */
  advancedSeoData?: SeoSchema;
  /**
   * Hidden from SEO Site Map
   * @readonly
   */
  hidden?: boolean | null;
}

/**
 * The SEO schema object contains data about different types of meta tags. It makes sure that the information about your page is presented properly to search engines.
 * The search engines use this information for ranking purposes, or to display snippets in the search results.
 * This data will override other sources of tags (for example patterns) and will be included in the <head> section of the HTML document, while not being displayed on the page itself.
 */
export interface SeoSchema {
  /** SEO tag information. */
  tags?: Tag[];
  /** SEO general settings. */
  settings?: Settings;
}

export interface Keyword {
  /** Keyword value. */
  term?: string;
  /** Whether the keyword is the main focus keyword. */
  isMain?: boolean;
  /**
   * The source that added the keyword terms to the SEO settings.
   * @maxLength 1000
   */
  origin?: string | null;
}

export interface Tag {
  /**
   * SEO tag type.
   *
   *
   * Supported values: `title`, `meta`, `script`, `link`.
   */
  type?: string;
  /**
   * A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.
   * For example: `{"name": "description", "content": "the description itself"}`.
   */
  props?: Record<string, any> | null;
  /** SEO tag metadata. For example, `{"height": 300, "width": 240}`. */
  meta?: Record<string, any> | null;
  /** SEO tag inner content. For example, `<title> inner content </title>`. */
  children?: string;
  /** Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages). */
  custom?: boolean;
  /** Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines. */
  disabled?: boolean;
}

export interface Settings {
  /**
   * Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.
   *
   *
   * Default: `false` (automatical redirect is enabled).
   */
  preventAutoRedirect?: boolean;
  /**
   * User-selected keyword terms for a specific page.
   * @maxSize 5
   */
  keywords?: Keyword[];
}

export interface Agenda {
  /** Whether the schedule is enabled for the event. */
  enabled?: boolean;
  /**
   * Agenda page URL.
   * @readonly
   */
  pageUrl?: SiteUrl;
}

/**
 * A Category is a classification object that groups related events on a site so you can organize and display them by themes, venues, or other facets.
 *
 * You can manage Categories, assign events to them, and use them to control the selection and order of events across different pages and widgets.
 *
 * Read more about [Categories](https://support.wix.com/en/article/creating-and-displaying-event-categories).
 */
export interface Category {
  /**
   * Category ID.
   * @format GUID
   * @readonly
   */
  id?: string;
  /**
   * Category name.
   * @minLength 1
   * @maxLength 30
   */
  name?: string;
  /**
   * Date and time when category was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * The total number of draft and published events assigned to the category.
   * @readonly
   */
  counts?: CategoryCounts;
  /**
   * Category state.
   *
   * Default: `MANUAL`.
   *
   * **Note:** The WIX_EVENTS.MANAGE_AUTO_CATEGORIES permission scope is required to use states other than `MANUAL`.
   * @maxSize 3
   */
  states?: StateWithLiterals[];
}

export interface CategoryCounts {
  /** Total number of draft events assigned to the category. */
  assignedEventsCount?: number | null;
  /** Total number of published events assigned to the category. Deleted events are excluded. */
  assignedDraftEventsCount?: number | null;
}

export enum State {
  /** Categoty is created manually by the user. */
  MANUAL = 'MANUAL',
  /** Category is created automatically. */
  AUTO = 'AUTO',
  /** Category is created automatically when publishing recurring events. */
  RECURRING_EVENT = 'RECURRING_EVENT',
  /** Category is hidden. */
  HIDDEN = 'HIDDEN',
}

/** @enumType */
export type StateWithLiterals =
  | State
  | 'MANUAL'
  | 'AUTO'
  | 'RECURRING_EVENT'
  | 'HIDDEN';

export interface EventDisplaySettings {
  /** Whether event details button is hidden. Only available for events with no registration. */
  hideEventDetailsButton?: boolean | null;
  /** Disables event details page visibility. If event has an external registration configured visitors will be redirected from this page. */
  hideEventDetailsPage?: boolean | null;
}

export interface LabellingSettings {}

export interface RichContent {
  /** Node objects representing a rich content document. */
  nodes?: Node[];
  /** Object metadata. */
  metadata?: Metadata;
  /** Global styling for header, paragraph, block quote, and code block nodes in the object. */
  documentStyle?: DocumentStyle;
}

export interface Node extends NodeDataOneOf {
  /** Data for a button node. */
  buttonData?: ButtonData;
  /** Data for a code block node. */
  codeBlockData?: CodeBlockData;
  /** Data for a divider node. */
  dividerData?: DividerData;
  /** Data for a file node. */
  fileData?: FileData;
  /** Data for a gallery node. */
  galleryData?: GalleryData;
  /** Data for a GIF node. */
  gifData?: GIFData;
  /** Data for a heading node. */
  headingData?: HeadingData;
  /** Data for an embedded HTML node. */
  htmlData?: HTMLData;
  /** Data for an image node. */
  imageData?: ImageData;
  /** Data for a link preview node. */
  linkPreviewData?: LinkPreviewData;
  /** @deprecated */
  mapData?: MapData;
  /** Data for a paragraph node. */
  paragraphData?: ParagraphData;
  /** Data for a poll node. */
  pollData?: PollData;
  /** Data for a text node. Used to apply decorations to text. */
  textData?: TextData;
  /** Data for an app embed node. */
  appEmbedData?: AppEmbedData;
  /** Data for a video node. */
  videoData?: VideoData;
  /** Data for an oEmbed node. */
  embedData?: EmbedData;
  /** Data for a collapsible list node. */
  collapsibleListData?: CollapsibleListData;
  /** Data for a table node. */
  tableData?: TableData;
  /** Data for a table cell node. */
  tableCellData?: TableCellData;
  /** Data for a custom external node. */
  externalData?: Record<string, any> | null;
  /** Data for an audio node. */
  audioData?: AudioData;
  /** Data for an ordered list node. */
  orderedListData?: OrderedListData;
  /** Data for a bulleted list node. */
  bulletedListData?: BulletedListData;
  /** Data for a block quote node. */
  blockquoteData?: BlockquoteData;
  /** Data for a caption node. */
  captionData?: CaptionData;
  /** Data for a layout node. Reserved for future use. */
  layoutData?: LayoutData;
  /** Data for a cell node. */
  layoutCellData?: LayoutCellData;
  /** Data for a shape node. */
  shapeData?: ShapeData;
  /** Node type. Use `APP_EMBED` for nodes that embed content from other Wix apps. Use `EMBED` to embed content in [oEmbed](https://oembed.com/) format. */
  type?: NodeTypeWithLiterals;
  /** Node ID. */
  id?: string;
  /** A list of child nodes. */
  nodes?: Node[];
  /** Padding and background color styling for the node. */
  style?: NodeStyle;
}

/** @oneof */
export interface NodeDataOneOf {
  /** Data for a button node. */
  buttonData?: ButtonData;
  /** Data for a code block node. */
  codeBlockData?: CodeBlockData;
  /** Data for a divider node. */
  dividerData?: DividerData;
  /** Data for a file node. */
  fileData?: FileData;
  /** Data for a gallery node. */
  galleryData?: GalleryData;
  /** Data for a GIF node. */
  gifData?: GIFData;
  /** Data for a heading node. */
  headingData?: HeadingData;
  /** Data for an embedded HTML node. */
  htmlData?: HTMLData;
  /** Data for an image node. */
  imageData?: ImageData;
  /** Data for a link preview node. */
  linkPreviewData?: LinkPreviewData;
  /** @deprecated */
  mapData?: MapData;
  /** Data for a paragraph node. */
  paragraphData?: ParagraphData;
  /** Data for a poll node. */
  pollData?: PollData;
  /** Data for a text node. Used to apply decorations to text. */
  textData?: TextData;
  /** Data for an app embed node. */
  appEmbedData?: AppEmbedData;
  /** Data for a video node. */
  videoData?: VideoData;
  /** Data for an oEmbed node. */
  embedData?: EmbedData;
  /** Data for a collapsible list node. */
  collapsibleListData?: CollapsibleListData;
  /** Data for a table node. */
  tableData?: TableData;
  /** Data for a table cell node. */
  tableCellData?: TableCellData;
  /** Data for a custom external node. */
  externalData?: Record<string, any> | null;
  /** Data for an audio node. */
  audioData?: AudioData;
  /** Data for an ordered list node. */
  orderedListData?: OrderedListData;
  /** Data for a bulleted list node. */
  bulletedListData?: BulletedListData;
  /** Data for a block quote node. */
  blockquoteData?: BlockquoteData;
  /** Data for a caption node. */
  captionData?: CaptionData;
  /** Data for a layout node. Reserved for future use. */
  layoutData?: LayoutData;
  /** Data for a cell node. */
  layoutCellData?: LayoutCellData;
  /** Data for a shape node. */
  shapeData?: ShapeData;
}

export enum NodeType {
  PARAGRAPH = 'PARAGRAPH',
  TEXT = 'TEXT',
  HEADING = 'HEADING',
  BULLETED_LIST = 'BULLETED_LIST',
  ORDERED_LIST = 'ORDERED_LIST',
  LIST_ITEM = 'LIST_ITEM',
  BLOCKQUOTE = 'BLOCKQUOTE',
  CODE_BLOCK = 'CODE_BLOCK',
  VIDEO = 'VIDEO',
  DIVIDER = 'DIVIDER',
  FILE = 'FILE',
  GALLERY = 'GALLERY',
  GIF = 'GIF',
  HTML = 'HTML',
  IMAGE = 'IMAGE',
  LINK_PREVIEW = 'LINK_PREVIEW',
  /** @deprecated */
  MAP = 'MAP',
  POLL = 'POLL',
  APP_EMBED = 'APP_EMBED',
  BUTTON = 'BUTTON',
  COLLAPSIBLE_LIST = 'COLLAPSIBLE_LIST',
  TABLE = 'TABLE',
  EMBED = 'EMBED',
  COLLAPSIBLE_ITEM = 'COLLAPSIBLE_ITEM',
  COLLAPSIBLE_ITEM_TITLE = 'COLLAPSIBLE_ITEM_TITLE',
  COLLAPSIBLE_ITEM_BODY = 'COLLAPSIBLE_ITEM_BODY',
  TABLE_CELL = 'TABLE_CELL',
  TABLE_ROW = 'TABLE_ROW',
  EXTERNAL = 'EXTERNAL',
  AUDIO = 'AUDIO',
  CAPTION = 'CAPTION',
  LAYOUT = 'LAYOUT',
  LAYOUT_CELL = 'LAYOUT_CELL',
  SHAPE = 'SHAPE',
}

/** @enumType */
export type NodeTypeWithLiterals =
  | NodeType
  | 'PARAGRAPH'
  | 'TEXT'
  | 'HEADING'
  | 'BULLETED_LIST'
  | 'ORDERED_LIST'
  | 'LIST_ITEM'
  | 'BLOCKQUOTE'
  | 'CODE_BLOCK'
  | 'VIDEO'
  | 'DIVIDER'
  | 'FILE'
  | 'GALLERY'
  | 'GIF'
  | 'HTML'
  | 'IMAGE'
  | 'LINK_PREVIEW'
  | 'MAP'
  | 'POLL'
  | 'APP_EMBED'
  | 'BUTTON'
  | 'COLLAPSIBLE_LIST'
  | 'TABLE'
  | 'EMBED'
  | 'COLLAPSIBLE_ITEM'
  | 'COLLAPSIBLE_ITEM_TITLE'
  | 'COLLAPSIBLE_ITEM_BODY'
  | 'TABLE_CELL'
  | 'TABLE_ROW'
  | 'EXTERNAL'
  | 'AUDIO'
  | 'CAPTION'
  | 'LAYOUT'
  | 'LAYOUT_CELL'
  | 'SHAPE';

export interface NodeStyle {
  /** The top padding value in pixels. */
  paddingTop?: string | null;
  /** The bottom padding value in pixels. */
  paddingBottom?: string | null;
  /** The background color as a hexadecimal value. */
  backgroundColor?: string | null;
}

export interface ButtonData {
  /** Styling for the button's container. */
  containerData?: PluginContainerData;
  /** The button type. */
  type?: ButtonDataTypeWithLiterals;
  /** Styling for the button. */
  styles?: Styles;
  /** The text to display on the button. */
  text?: string | null;
  /** Button link details. */
  link?: Link;
}

export interface Border {
  /**
   * Deprecated: Use `borderWidth` in `styles` instead.
   * @deprecated
   */
  width?: number | null;
  /**
   * Deprecated: Use `borderRadius` in `styles` instead.
   * @deprecated
   */
  radius?: number | null;
}

export interface Colors {
  /**
   * Deprecated: Use `textColor` in `styles` instead.
   * @deprecated
   */
  text?: string | null;
  /**
   * Deprecated: Use `borderColor` in `styles` instead.
   * @deprecated
   */
  border?: string | null;
  /**
   * Deprecated: Use `backgroundColor` in `styles` instead.
   * @deprecated
   */
  background?: string | null;
}

export interface PluginContainerData {
  /** The width of the node when it's displayed. */
  width?: PluginContainerDataWidth;
  /** The node's alignment within its container. */
  alignment?: PluginContainerDataAlignmentWithLiterals;
  /** Spoiler cover settings for the node. */
  spoiler?: Spoiler;
  /** The height of the node when it's displayed. */
  height?: Height;
  /** Sets whether text should wrap around this node when it's displayed. If `textWrap` is `false`, the node takes up the width of its container. Defaults to `true` for all node types except 'DIVIVDER' where it defaults to `false`. */
  textWrap?: boolean | null;
}

export enum WidthType {
  /** Width matches the content width */
  CONTENT = 'CONTENT',
  /** Small Width */
  SMALL = 'SMALL',
  /** Width will match the original asset width */
  ORIGINAL = 'ORIGINAL',
  /** coast-to-coast display */
  FULL_WIDTH = 'FULL_WIDTH',
}

/** @enumType */
export type WidthTypeWithLiterals =
  | WidthType
  | 'CONTENT'
  | 'SMALL'
  | 'ORIGINAL'
  | 'FULL_WIDTH';

export interface PluginContainerDataWidth
  extends PluginContainerDataWidthDataOneOf {
  /**
   * One of the following predefined width options:
   * `CONTENT`: The width of the container matches the content width.
   * `SMALL`: A small width.
   * `ORIGINAL`: For `imageData` containers only. The width of the container matches the original image width.
   * `FULL_WIDTH`: For `imageData` containers only. The image container takes up the full width of the screen.
   */
  size?: WidthTypeWithLiterals;
  /** A custom width value in pixels. */
  custom?: string | null;
}

/** @oneof */
export interface PluginContainerDataWidthDataOneOf {
  /**
   * One of the following predefined width options:
   * `CONTENT`: The width of the container matches the content width.
   * `SMALL`: A small width.
   * `ORIGINAL`: For `imageData` containers only. The width of the container matches the original image width.
   * `FULL_WIDTH`: For `imageData` containers only. The image container takes up the full width of the screen.
   */
  size?: WidthTypeWithLiterals;
  /** A custom width value in pixels. */
  custom?: string | null;
}

export enum PluginContainerDataAlignment {
  /** Center Alignment */
  CENTER = 'CENTER',
  /** Left Alignment */
  LEFT = 'LEFT',
  /** Right Alignment */
  RIGHT = 'RIGHT',
}

/** @enumType */
export type PluginContainerDataAlignmentWithLiterals =
  | PluginContainerDataAlignment
  | 'CENTER'
  | 'LEFT'
  | 'RIGHT';

export interface Spoiler {
  /** Sets whether the spoiler cover is enabled for this node. Defaults to `false`. */
  enabled?: boolean | null;
  /** The description displayed on top of the spoiler cover. */
  description?: string | null;
  /** The text for the button used to remove the spoiler cover. */
  buttonText?: string | null;
}

export interface Height {
  /** A custom height value in pixels. */
  custom?: string | null;
}

export enum ButtonDataType {
  /** Regular link button */
  LINK = 'LINK',
  /** Triggers custom action that is defined in plugin configuration by the consumer */
  ACTION = 'ACTION',
}

/** @enumType */
export type ButtonDataTypeWithLiterals = ButtonDataType | 'LINK' | 'ACTION';

export interface Styles {
  /**
   * Deprecated: Use `borderWidth` and `borderRadius` instead.
   * @deprecated
   */
  border?: Border;
  /**
   * Deprecated: Use `textColor`, `borderColor` and `backgroundColor` instead.
   * @deprecated
   */
  colors?: Colors;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /**
   * Deprecated: Use `borderWidth` for normal/hover states instead.
   * @deprecated
   */
  borderWidthHover?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /**
   * Border color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  borderColorHover?: string | null;
  /**
   * Text color as a hexadecimal value.
   * @format COLOR_HEX
   */
  textColor?: string | null;
  /**
   * Text color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  textColorHover?: string | null;
  /**
   * Background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /**
   * Background color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  backgroundColorHover?: string | null;
  /** Button size option, one of `SMALL`, `MEDIUM` or `LARGE`. Defaults to `MEDIUM`. */
  buttonSize?: string | null;
}

export interface Link extends LinkDataOneOf {
  /** The absolute URL for the linked document. */
  url?: string;
  /** The target node's ID. Used for linking to another node in this object. */
  anchor?: string;
  /**
   * he HTML `target` attribute value for the link. This property defines where the linked document opens as follows:
   * `SELF` - Default. Opens the linked document in the same frame as the link.
   * `BLANK` - Opens the linked document in a new browser tab or window.
   * `PARENT` - Opens the linked document in the link's parent frame.
   * `TOP` - Opens the linked document in the full body of the link's browser tab or window.
   */
  target?: TargetWithLiterals;
  /** The HTML `rel` attribute value for the link. This object specifies the relationship between the current document and the linked document. */
  rel?: Rel;
  /** A serialized object used for a custom or external link panel. */
  customData?: string | null;
}

/** @oneof */
export interface LinkDataOneOf {
  /** The absolute URL for the linked document. */
  url?: string;
  /** The target node's ID. Used for linking to another node in this object. */
  anchor?: string;
}

export enum Target {
  /** Opens the linked document in the same frame as it was clicked (this is default) */
  SELF = 'SELF',
  /** Opens the linked document in a new window or tab */
  BLANK = 'BLANK',
  /** Opens the linked document in the parent frame */
  PARENT = 'PARENT',
  /** Opens the linked document in the full body of the window */
  TOP = 'TOP',
}

/** @enumType */
export type TargetWithLiterals = Target | 'SELF' | 'BLANK' | 'PARENT' | 'TOP';

export interface Rel {
  /** Indicates to search engine crawlers not to follow the link. Defaults to `false`. */
  nofollow?: boolean | null;
  /** Indicates to search engine crawlers that the link is a paid placement such as sponsored content or an advertisement. Defaults to `false`. */
  sponsored?: boolean | null;
  /** Indicates that this link is user-generated content and isn't necessarily trusted or endorsed by the page’s author. For example, a link in a fourm post. Defaults to `false`. */
  ugc?: boolean | null;
  /** Indicates that this link protect referral information from being passed to the target website. */
  noreferrer?: boolean | null;
}

export interface CodeBlockData {
  /** Styling for the code block's text. */
  textStyle?: TextStyle;
}

export interface TextStyle {
  /** Text alignment. Defaults to `AUTO`. */
  textAlignment?: TextAlignmentWithLiterals;
  /** A CSS `line-height` value for the text expressed as a ratio relative to the font size. For example, if the font size is 20px, a `lineHeight` value of `'1.5'`` results in a line height of 30px. */
  lineHeight?: string | null;
}

export enum TextAlignment {
  /** browser default, eqivalent to `initial` */
  AUTO = 'AUTO',
  /** Left align */
  LEFT = 'LEFT',
  /** Right align */
  RIGHT = 'RIGHT',
  /** Center align */
  CENTER = 'CENTER',
  /** Text is spaced to line up its left and right edges to the left and right edges of the line box, except for the last line */
  JUSTIFY = 'JUSTIFY',
}

/** @enumType */
export type TextAlignmentWithLiterals =
  | TextAlignment
  | 'AUTO'
  | 'LEFT'
  | 'RIGHT'
  | 'CENTER'
  | 'JUSTIFY';

export interface DividerData {
  /** Styling for the divider's container. */
  containerData?: PluginContainerData;
  /** Divider line style. */
  lineStyle?: LineStyleWithLiterals;
  /** Divider width. */
  width?: WidthWithLiterals;
  /** Divider alignment. */
  alignment?: DividerDataAlignmentWithLiterals;
}

export enum LineStyle {
  /** Single Line */
  SINGLE = 'SINGLE',
  /** Double Line */
  DOUBLE = 'DOUBLE',
  /** Dashed Line */
  DASHED = 'DASHED',
  /** Dotted Line */
  DOTTED = 'DOTTED',
}

/** @enumType */
export type LineStyleWithLiterals =
  | LineStyle
  | 'SINGLE'
  | 'DOUBLE'
  | 'DASHED'
  | 'DOTTED';

export enum Width {
  /** Large line */
  LARGE = 'LARGE',
  /** Medium line */
  MEDIUM = 'MEDIUM',
  /** Small line */
  SMALL = 'SMALL',
}

/** @enumType */
export type WidthWithLiterals = Width | 'LARGE' | 'MEDIUM' | 'SMALL';

export enum DividerDataAlignment {
  /** Center alignment */
  CENTER = 'CENTER',
  /** Left alignment */
  LEFT = 'LEFT',
  /** Right alignment */
  RIGHT = 'RIGHT',
}

/** @enumType */
export type DividerDataAlignmentWithLiterals =
  | DividerDataAlignment
  | 'CENTER'
  | 'LEFT'
  | 'RIGHT';

export interface FileData {
  /** Styling for the file's container. */
  containerData?: PluginContainerData;
  /** The source for the file's data. */
  src?: FileSource;
  /** File name. */
  name?: string | null;
  /** File type. */
  type?: string | null;
  /**
   * Use `sizeInKb` instead.
   * @deprecated
   */
  size?: number | null;
  /** Settings for PDF files. */
  pdfSettings?: PDFSettings;
  /** File MIME type. */
  mimeType?: string | null;
  /** File path. */
  path?: string | null;
  /** File size in KB. */
  sizeInKb?: string | null;
}

export enum ViewMode {
  /** No PDF view */
  NONE = 'NONE',
  /** Full PDF view */
  FULL = 'FULL',
  /** Mini PDF view */
  MINI = 'MINI',
}

/** @enumType */
export type ViewModeWithLiterals = ViewMode | 'NONE' | 'FULL' | 'MINI';

export interface FileSource extends FileSourceDataOneOf {
  /** The absolute URL for the file's source. */
  url?: string | null;
  /**
   * Custom ID. Use `id` instead.
   * @deprecated
   */
  custom?: string | null;
  /** An ID that's resolved to a URL by a resolver function. */
  id?: string | null;
  /** Indicates whether the file's source is private. Defaults to `false`. */
  private?: boolean | null;
}

/** @oneof */
export interface FileSourceDataOneOf {
  /** The absolute URL for the file's source. */
  url?: string | null;
  /**
   * Custom ID. Use `id` instead.
   * @deprecated
   */
  custom?: string | null;
  /** An ID that's resolved to a URL by a resolver function. */
  id?: string | null;
}

export interface PDFSettings {
  /**
   * PDF view mode. One of the following:
   * `NONE` : The PDF isn't displayed.
   * `FULL` : A full page view of the PDF is displayed.
   * `MINI` : A mini view of the PDF is displayed.
   */
  viewMode?: ViewModeWithLiterals;
  /** Sets whether the PDF download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Sets whether the PDF print button is disabled. Defaults to `false`. */
  disablePrint?: boolean | null;
}

export interface GalleryData {
  /** Styling for the gallery's container. */
  containerData?: PluginContainerData;
  /** The items in the gallery. */
  items?: Item[];
  /** Options for defining the gallery's appearance. */
  options?: GalleryOptions;
  /** Sets whether the gallery's expand button is disabled. Defaults to `false`. */
  disableExpand?: boolean | null;
  /** Sets whether the gallery's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
}

export interface Media {
  /** The source for the media's data. */
  src?: FileSource;
  /** Media width in pixels. */
  width?: number | null;
  /** Media height in pixels. */
  height?: number | null;
  /** Media duration in seconds. Only relevant for audio and video files. */
  duration?: number | null;
}

export interface ItemImage {
  /** Image file details. */
  media?: Media;
  /** Link details for images that are links. */
  link?: Link;
}

export interface Video {
  /** Video file details. */
  media?: Media;
  /** Video thumbnail file details. */
  thumbnail?: Media;
}

export interface Item extends ItemDataOneOf {
  /** An image item. */
  image?: ItemImage;
  /** A video item. */
  video?: Video;
  /** Item title. */
  title?: string | null;
  /** Item's alternative text. */
  altText?: string | null;
}

/** @oneof */
export interface ItemDataOneOf {
  /** An image item. */
  image?: ItemImage;
  /** A video item. */
  video?: Video;
}

export interface GalleryOptions {
  /** Gallery layout. */
  layout?: GalleryOptionsLayout;
  /** Styling for gallery items. */
  item?: ItemStyle;
  /** Styling for gallery thumbnail images. */
  thumbnails?: Thumbnails;
}

export enum LayoutType {
  /** Collage type */
  COLLAGE = 'COLLAGE',
  /** Masonry type */
  MASONRY = 'MASONRY',
  /** Grid type */
  GRID = 'GRID',
  /** Thumbnail type */
  THUMBNAIL = 'THUMBNAIL',
  /** Slider type */
  SLIDER = 'SLIDER',
  /** Slideshow type */
  SLIDESHOW = 'SLIDESHOW',
  /** Panorama type */
  PANORAMA = 'PANORAMA',
  /** Column type */
  COLUMN = 'COLUMN',
  /** Magic type */
  MAGIC = 'MAGIC',
  /** Fullsize images type */
  FULLSIZE = 'FULLSIZE',
}

/** @enumType */
export type LayoutTypeWithLiterals =
  | LayoutType
  | 'COLLAGE'
  | 'MASONRY'
  | 'GRID'
  | 'THUMBNAIL'
  | 'SLIDER'
  | 'SLIDESHOW'
  | 'PANORAMA'
  | 'COLUMN'
  | 'MAGIC'
  | 'FULLSIZE';

export enum Orientation {
  /** Rows Orientation */
  ROWS = 'ROWS',
  /** Columns Orientation */
  COLUMNS = 'COLUMNS',
}

/** @enumType */
export type OrientationWithLiterals = Orientation | 'ROWS' | 'COLUMNS';

export enum Crop {
  /** Crop to fill */
  FILL = 'FILL',
  /** Crop to fit */
  FIT = 'FIT',
}

/** @enumType */
export type CropWithLiterals = Crop | 'FILL' | 'FIT';

export enum ThumbnailsAlignment {
  /** Top alignment */
  TOP = 'TOP',
  /** Right alignment */
  RIGHT = 'RIGHT',
  /** Bottom alignment */
  BOTTOM = 'BOTTOM',
  /** Left alignment */
  LEFT = 'LEFT',
  /** No thumbnail */
  NONE = 'NONE',
}

/** @enumType */
export type ThumbnailsAlignmentWithLiterals =
  | ThumbnailsAlignment
  | 'TOP'
  | 'RIGHT'
  | 'BOTTOM'
  | 'LEFT'
  | 'NONE';

export interface GalleryOptionsLayout {
  /** Gallery layout type. */
  type?: LayoutTypeWithLiterals;
  /** Sets whether horizontal scroll is enabled. Defaults to `true` unless the layout `type` is set to `GRID` or `COLLAGE`. */
  horizontalScroll?: boolean | null;
  /** Gallery orientation. */
  orientation?: OrientationWithLiterals;
  /** The number of columns to display on full size screens. */
  numberOfColumns?: number | null;
  /** The number of columns to display on mobile screens. */
  mobileNumberOfColumns?: number | null;
}

export interface ItemStyle {
  /** Desirable dimension for each item in pixels (behvaior changes according to gallery type) */
  targetSize?: number | null;
  /** Item ratio */
  ratio?: number | null;
  /** Sets how item images are cropped. */
  crop?: CropWithLiterals;
  /** The spacing between items in pixels. */
  spacing?: number | null;
}

export interface Thumbnails {
  /** Thumbnail alignment. */
  placement?: ThumbnailsAlignmentWithLiterals;
  /** Spacing between thumbnails in pixels. */
  spacing?: number | null;
}

export interface GIFData {
  /** Styling for the GIF's container. */
  containerData?: PluginContainerData;
  /** The source of the full size GIF. */
  original?: GIF;
  /** The source of the downsized GIF. */
  downsized?: GIF;
  /** Height in pixels. */
  height?: number;
  /** Width in pixels. */
  width?: number;
  /** Type of GIF (Sticker or NORMAL). Defaults to `NORMAL`. */
  gifType?: GIFTypeWithLiterals;
}

export interface GIF {
  /**
   * GIF format URL.
   * @format WEB_URL
   */
  gif?: string | null;
  /**
   * MP4 format URL.
   * @format WEB_URL
   */
  mp4?: string | null;
  /**
   * Thumbnail URL.
   * @format WEB_URL
   */
  still?: string | null;
}

export enum GIFType {
  NORMAL = 'NORMAL',
  STICKER = 'STICKER',
}

/** @enumType */
export type GIFTypeWithLiterals = GIFType | 'NORMAL' | 'STICKER';

export interface HeadingData {
  /** Heading level from 1-6. */
  level?: number;
  /** Styling for the heading text. */
  textStyle?: TextStyle;
  /** Indentation level from 1-4. */
  indentation?: number | null;
}

export interface HTMLData extends HTMLDataDataOneOf {
  /** The URL for the HTML code for the node. */
  url?: string;
  /** The HTML code for the node. */
  html?: string;
  /**
   * Whether this is an AdSense element. Use `source` instead.
   * @deprecated
   */
  isAdsense?: boolean | null;
  /** Styling for the HTML node's container. Height property is irrelevant for HTML embeds when autoHeight is set to `true`. */
  containerData?: PluginContainerData;
  /** The type of HTML code. */
  source?: SourceWithLiterals;
  /** If container height is aligned with its content height. Defaults to `true`. */
  autoHeight?: boolean | null;
}

/** @oneof */
export interface HTMLDataDataOneOf {
  /** The URL for the HTML code for the node. */
  url?: string;
  /** The HTML code for the node. */
  html?: string;
  /**
   * Whether this is an AdSense element. Use `source` instead.
   * @deprecated
   */
  isAdsense?: boolean | null;
}

export enum Source {
  HTML = 'HTML',
  ADSENSE = 'ADSENSE',
  AI = 'AI',
}

/** @enumType */
export type SourceWithLiterals = Source | 'HTML' | 'ADSENSE' | 'AI';

export interface ImageData {
  /** Styling for the image's container. */
  containerData?: PluginContainerData;
  /** Image file details. */
  image?: Media;
  /** Link details for images that are links. */
  link?: Link;
  /** Sets whether the image expands to full screen when clicked. Defaults to `false`. */
  disableExpand?: boolean | null;
  /** Image's alternative text. */
  altText?: string | null;
  /**
   * Deprecated: use Caption node instead.
   * @deprecated
   */
  caption?: string | null;
  /** Sets whether the image's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Sets whether the image is decorative and does not need an explanation. Defaults to `false`. */
  decorative?: boolean | null;
  /** Styling for the image. */
  styles?: ImageDataStyles;
}

export interface StylesBorder {
  /** Border width in pixels. */
  width?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Border radius in pixels. */
  radius?: number | null;
}

export interface ImageDataStyles {
  /** Border attributes. */
  border?: StylesBorder;
}

export interface LinkPreviewData {
  /** Styling for the link preview's container. */
  containerData?: PluginContainerData;
  /** Link details. */
  link?: Link;
  /** Preview title. */
  title?: string | null;
  /** Preview thumbnail URL. */
  thumbnailUrl?: string | null;
  /** Preview description. */
  description?: string | null;
  /** The preview content as HTML. */
  html?: string | null;
  /** Styling for the link preview. */
  styles?: LinkPreviewDataStyles;
}

export enum StylesPosition {
  /** Thumbnail positioned at the start (left in LTR layouts, right in RTL layouts) */
  START = 'START',
  /** Thumbnail positioned at the end (right in LTR layouts, left in RTL layouts) */
  END = 'END',
  /** Thumbnail positioned at the top */
  TOP = 'TOP',
  /** Thumbnail hidden and not displayed */
  HIDDEN = 'HIDDEN',
}

/** @enumType */
export type StylesPositionWithLiterals =
  | StylesPosition
  | 'START'
  | 'END'
  | 'TOP'
  | 'HIDDEN';

export interface LinkPreviewDataStyles {
  /**
   * Background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /**
   * Title color as a hexadecimal value.
   * @format COLOR_HEX
   */
  titleColor?: string | null;
  /**
   * Subtitle color as a hexadecimal value.
   * @format COLOR_HEX
   */
  subtitleColor?: string | null;
  /**
   * Link color as a hexadecimal value.
   * @format COLOR_HEX
   */
  linkColor?: string | null;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /** Position of thumbnail. Defaults to `START`. */
  thumbnailPosition?: StylesPositionWithLiterals;
}

export interface MapData {
  /** Styling for the map's container. */
  containerData?: PluginContainerData;
  /** Map settings. */
  mapSettings?: MapSettings;
}

export interface MapSettings {
  /** The address to display on the map. */
  address?: string | null;
  /** Sets whether the map is draggable. */
  draggable?: boolean | null;
  /** Sets whether the location marker is visible. */
  marker?: boolean | null;
  /** Sets whether street view control is enabled. */
  streetViewControl?: boolean | null;
  /** Sets whether zoom control is enabled. */
  zoomControl?: boolean | null;
  /** Location latitude. */
  lat?: number | null;
  /** Location longitude. */
  lng?: number | null;
  /** Location name. */
  locationName?: string | null;
  /** Sets whether view mode control is enabled. */
  viewModeControl?: boolean | null;
  /** Initial zoom value. */
  initialZoom?: number | null;
  /** Map type. `HYBRID` is a combination of the `ROADMAP` and `SATELLITE` map types. */
  mapType?: MapTypeWithLiterals;
}

export enum MapType {
  /** Roadmap map type */
  ROADMAP = 'ROADMAP',
  /** Satellite map type */
  SATELITE = 'SATELITE',
  /** Hybrid map type */
  HYBRID = 'HYBRID',
  /** Terrain map type */
  TERRAIN = 'TERRAIN',
}

/** @enumType */
export type MapTypeWithLiterals =
  | MapType
  | 'ROADMAP'
  | 'SATELITE'
  | 'HYBRID'
  | 'TERRAIN';

export interface ParagraphData {
  /** Styling for the paragraph text. */
  textStyle?: TextStyle;
  /** Indentation level from 1-4. */
  indentation?: number | null;
  /** Paragraph level */
  level?: number | null;
}

export interface PollData {
  /** Styling for the poll's container. */
  containerData?: PluginContainerData;
  /** Poll data. */
  poll?: Poll;
  /** Layout settings for the poll and voting options. */
  layout?: PollDataLayout;
  /** Styling for the poll and voting options. */
  design?: Design;
}

export enum ViewRole {
  /** Only Poll creator can view the results */
  CREATOR = 'CREATOR',
  /** Anyone who voted can see the results */
  VOTERS = 'VOTERS',
  /** Anyone can see the results, even if one didn't vote */
  EVERYONE = 'EVERYONE',
}

/** @enumType */
export type ViewRoleWithLiterals = ViewRole | 'CREATOR' | 'VOTERS' | 'EVERYONE';

export enum VoteRole {
  /** Logged in member */
  SITE_MEMBERS = 'SITE_MEMBERS',
  /** Anyone */
  ALL = 'ALL',
}

/** @enumType */
export type VoteRoleWithLiterals = VoteRole | 'SITE_MEMBERS' | 'ALL';

export interface Permissions {
  /** Sets who can view the poll results. */
  view?: ViewRoleWithLiterals;
  /** Sets who can vote. */
  vote?: VoteRoleWithLiterals;
  /** Sets whether one voter can vote multiple times. Defaults to `false`. */
  allowMultipleVotes?: boolean | null;
}

export interface Option {
  /** Option ID. */
  id?: string | null;
  /** Option title. */
  title?: string | null;
  /** The image displayed with the option. */
  image?: Media;
}

export interface PollSettings {
  /** Permissions settings for voting. */
  permissions?: Permissions;
  /** Sets whether voters are displayed in the vote results. Defaults to `true`. */
  showVoters?: boolean | null;
  /** Sets whether the vote count is displayed. Defaults to `true`. */
  showVotesCount?: boolean | null;
}

export enum PollLayoutType {
  /** List */
  LIST = 'LIST',
  /** Grid */
  GRID = 'GRID',
}

/** @enumType */
export type PollLayoutTypeWithLiterals = PollLayoutType | 'LIST' | 'GRID';

export enum PollLayoutDirection {
  /** Left-to-right */
  LTR = 'LTR',
  /** Right-to-left */
  RTL = 'RTL',
}

/** @enumType */
export type PollLayoutDirectionWithLiterals =
  | PollLayoutDirection
  | 'LTR'
  | 'RTL';

export interface PollLayout {
  /** The layout for displaying the voting options. */
  type?: PollLayoutTypeWithLiterals;
  /** The direction of the text displayed in the voting options. Text can be displayed either right-to-left or left-to-right. */
  direction?: PollLayoutDirectionWithLiterals;
  /** Sets whether to display the main poll image. Defaults to `false`. */
  enableImage?: boolean | null;
}

export interface OptionLayout {
  /** Sets whether to display option images. Defaults to `false`. */
  enableImage?: boolean | null;
}

export enum BackgroundType {
  /** Color background type */
  COLOR = 'COLOR',
  /** Image background type */
  IMAGE = 'IMAGE',
  /** Gradiant background type */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type BackgroundTypeWithLiterals =
  | BackgroundType
  | 'COLOR'
  | 'IMAGE'
  | 'GRADIENT';

export interface Gradient {
  /** The gradient angle in degrees. */
  angle?: number | null;
  /**
   * The start color as a hexademical value.
   * @format COLOR_HEX
   */
  startColor?: string | null;
  /**
   * The end color as a hexademical value.
   * @format COLOR_HEX
   */
  lastColor?: string | null;
}

export interface Background extends BackgroundBackgroundOneOf {
  /**
   * The background color as a hexademical value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** An image to use for the background. */
  image?: Media;
  /** Details for a gradient background. */
  gradient?: Gradient;
  /** Background type. For each option, include the relevant details. */
  type?: BackgroundTypeWithLiterals;
}

/** @oneof */
export interface BackgroundBackgroundOneOf {
  /**
   * The background color as a hexademical value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** An image to use for the background. */
  image?: Media;
  /** Details for a gradient background. */
  gradient?: Gradient;
}

export interface PollDesign {
  /** Background styling. */
  background?: Background;
  /** Border radius in pixels. */
  borderRadius?: number | null;
}

export interface OptionDesign {
  /** Border radius in pixels. */
  borderRadius?: number | null;
}

export interface Poll {
  /** Poll ID. */
  id?: string | null;
  /** Poll title. */
  title?: string | null;
  /** Poll creator ID. */
  creatorId?: string | null;
  /** Main poll image. */
  image?: Media;
  /** Voting options. */
  options?: Option[];
  /** The poll's permissions and display settings. */
  settings?: PollSettings;
}

export interface PollDataLayout {
  /** Poll layout settings. */
  poll?: PollLayout;
  /** Voting otpions layout settings. */
  options?: OptionLayout;
}

export interface Design {
  /** Styling for the poll. */
  poll?: PollDesign;
  /** Styling for voting options. */
  options?: OptionDesign;
}

export interface TextData {
  /** The text to apply decorations to. */
  text?: string;
  /** The decorations to apply. */
  decorations?: Decoration[];
}

/** Adds appearence changes to text */
export interface Decoration extends DecorationDataOneOf {
  /** Data for an anchor link decoration. */
  anchorData?: AnchorData;
  /** Data for a color decoration. */
  colorData?: ColorData;
  /** Data for an external link decoration. */
  linkData?: LinkData;
  /** Data for a mention decoration. */
  mentionData?: MentionData;
  /** Data for a font size decoration. */
  fontSizeData?: FontSizeData;
  /** Font weight for a bold decoration. */
  fontWeightValue?: number | null;
  /** Data for an italic decoration. Defaults to `true`. */
  italicData?: boolean | null;
  /** Data for an underline decoration. Defaults to `true`. */
  underlineData?: boolean | null;
  /** Data for a spoiler decoration. */
  spoilerData?: SpoilerData;
  /** Data for a strikethrough decoration. Defaults to `true`. */
  strikethroughData?: boolean | null;
  /** Data for a superscript decoration. Defaults to `true`. */
  superscriptData?: boolean | null;
  /** Data for a subscript decoration. Defaults to `true`. */
  subscriptData?: boolean | null;
  /** Data for a font family decoration. */
  fontFamilyData?: FontFamilyData;
  /** The type of decoration to apply. */
  type?: DecorationTypeWithLiterals;
}

/** @oneof */
export interface DecorationDataOneOf {
  /** Data for an anchor link decoration. */
  anchorData?: AnchorData;
  /** Data for a color decoration. */
  colorData?: ColorData;
  /** Data for an external link decoration. */
  linkData?: LinkData;
  /** Data for a mention decoration. */
  mentionData?: MentionData;
  /** Data for a font size decoration. */
  fontSizeData?: FontSizeData;
  /** Font weight for a bold decoration. */
  fontWeightValue?: number | null;
  /** Data for an italic decoration. Defaults to `true`. */
  italicData?: boolean | null;
  /** Data for an underline decoration. Defaults to `true`. */
  underlineData?: boolean | null;
  /** Data for a spoiler decoration. */
  spoilerData?: SpoilerData;
  /** Data for a strikethrough decoration. Defaults to `true`. */
  strikethroughData?: boolean | null;
  /** Data for a superscript decoration. Defaults to `true`. */
  superscriptData?: boolean | null;
  /** Data for a subscript decoration. Defaults to `true`. */
  subscriptData?: boolean | null;
  /** Data for a font family decoration. */
  fontFamilyData?: FontFamilyData;
}

export enum DecorationType {
  BOLD = 'BOLD',
  ITALIC = 'ITALIC',
  UNDERLINE = 'UNDERLINE',
  SPOILER = 'SPOILER',
  ANCHOR = 'ANCHOR',
  MENTION = 'MENTION',
  LINK = 'LINK',
  COLOR = 'COLOR',
  FONT_SIZE = 'FONT_SIZE',
  EXTERNAL = 'EXTERNAL',
  STRIKETHROUGH = 'STRIKETHROUGH',
  SUPERSCRIPT = 'SUPERSCRIPT',
  SUBSCRIPT = 'SUBSCRIPT',
  FONT_FAMILY = 'FONT_FAMILY',
}

/** @enumType */
export type DecorationTypeWithLiterals =
  | DecorationType
  | 'BOLD'
  | 'ITALIC'
  | 'UNDERLINE'
  | 'SPOILER'
  | 'ANCHOR'
  | 'MENTION'
  | 'LINK'
  | 'COLOR'
  | 'FONT_SIZE'
  | 'EXTERNAL'
  | 'STRIKETHROUGH'
  | 'SUPERSCRIPT'
  | 'SUBSCRIPT'
  | 'FONT_FAMILY';

export interface AnchorData {
  /** The target node's ID. */
  anchor?: string;
}

export interface ColorData {
  /** The text's background color as a hexadecimal value. */
  background?: string | null;
  /** The text's foreground color as a hexadecimal value. */
  foreground?: string | null;
}

export interface LinkData {
  /** Link details. */
  link?: Link;
}

export interface MentionData {
  /** The mentioned user's name. */
  name?: string;
  /** The version of the user's name that appears after the `@` character in the mention. */
  slug?: string;
  /** Mentioned user's ID. */
  id?: string | null;
}

export interface FontSizeData {
  /** The units used for the font size. */
  unit?: FontTypeWithLiterals;
  /** Font size value. */
  value?: number | null;
}

export enum FontType {
  PX = 'PX',
  EM = 'EM',
}

/** @enumType */
export type FontTypeWithLiterals = FontType | 'PX' | 'EM';

export interface SpoilerData {
  /** Spoiler ID. */
  id?: string | null;
}

export interface FontFamilyData {
  /** @maxLength 1000 */
  value?: string | null;
}

export interface AppEmbedData extends AppEmbedDataAppDataOneOf {
  /** Data for embedded Wix Bookings content. */
  bookingData?: BookingData;
  /** Data for embedded Wix Events content. */
  eventData?: EventData;
  /** The type of Wix App content being embedded. */
  type?: AppTypeWithLiterals;
  /** The ID of the embedded content. */
  itemId?: string | null;
  /** The name of the embedded content. */
  name?: string | null;
  /**
   * Deprecated: Use `image` instead.
   * @deprecated
   */
  imageSrc?: string | null;
  /** The URL for the embedded content. */
  url?: string | null;
  /** An image for the embedded content. */
  image?: Media;
  /** Whether to hide the image. */
  hideImage?: boolean | null;
  /** Whether to hide the title. */
  hideTitle?: boolean | null;
  /** Whether to hide the price. */
  hidePrice?: boolean | null;
  /** Whether to hide the description (Event and Booking). */
  hideDescription?: boolean | null;
  /** Whether to hide the date and time (Event). */
  hideDateTime?: boolean | null;
  /** Whether to hide the location (Event). */
  hideLocation?: boolean | null;
  /** Whether to hide the duration (Booking). */
  hideDuration?: boolean | null;
  /** Whether to hide the button. */
  hideButton?: boolean | null;
  /** Whether to hide the ribbon. */
  hideRibbon?: boolean | null;
  /** Button styling options. */
  buttonStyles?: ButtonStyles;
  /** Image styling options. */
  imageStyles?: ImageStyles;
  /** Ribbon styling options. */
  ribbonStyles?: RibbonStyles;
  /** Card styling options. */
  cardStyles?: CardStyles;
  /** Styling for the app embed's container. */
  containerData?: PluginContainerData;
  /** Pricing data for embedded Wix App content. */
  pricingData?: PricingData;
}

/** @oneof */
export interface AppEmbedDataAppDataOneOf {
  /** Data for embedded Wix Bookings content. */
  bookingData?: BookingData;
  /** Data for embedded Wix Events content. */
  eventData?: EventData;
}

export enum Position {
  /** Image positioned at the start (left in LTR layouts, right in RTL layouts) */
  START = 'START',
  /** Image positioned at the end (right in LTR layouts, left in RTL layouts) */
  END = 'END',
  /** Image positioned at the top */
  TOP = 'TOP',
}

/** @enumType */
export type PositionWithLiterals = Position | 'START' | 'END' | 'TOP';

export enum AspectRatio {
  /** 1:1 aspect ratio */
  SQUARE = 'SQUARE',
  /** 16:9 aspect ratio */
  RECTANGLE = 'RECTANGLE',
}

/** @enumType */
export type AspectRatioWithLiterals = AspectRatio | 'SQUARE' | 'RECTANGLE';

export enum Resizing {
  /** Fill the container, may crop the image */
  FILL = 'FILL',
  /** Fit the image within the container */
  FIT = 'FIT',
}

/** @enumType */
export type ResizingWithLiterals = Resizing | 'FILL' | 'FIT';

export enum Placement {
  /** Ribbon placed on the image */
  IMAGE = 'IMAGE',
  /** Ribbon placed on the product information */
  PRODUCT_INFO = 'PRODUCT_INFO',
}

/** @enumType */
export type PlacementWithLiterals = Placement | 'IMAGE' | 'PRODUCT_INFO';

export enum CardStylesType {
  /** Card with visible border and background */
  CONTAINED = 'CONTAINED',
  /** Card without visible border */
  FRAMELESS = 'FRAMELESS',
}

/** @enumType */
export type CardStylesTypeWithLiterals =
  | CardStylesType
  | 'CONTAINED'
  | 'FRAMELESS';

export enum Alignment {
  /** Content aligned to start (left in LTR layouts, right in RTL layouts) */
  START = 'START',
  /** Content centered */
  CENTER = 'CENTER',
  /** Content aligned to end (right in LTR layouts, left in RTL layouts) */
  END = 'END',
}

/** @enumType */
export type AlignmentWithLiterals = Alignment | 'START' | 'CENTER' | 'END';

export enum Layout {
  /** Elements stacked vertically */
  STACKED = 'STACKED',
  /** Elements arranged horizontally */
  SIDE_BY_SIDE = 'SIDE_BY_SIDE',
}

/** @enumType */
export type LayoutWithLiterals = Layout | 'STACKED' | 'SIDE_BY_SIDE';

export enum AppType {
  PRODUCT = 'PRODUCT',
  EVENT = 'EVENT',
  BOOKING = 'BOOKING',
}

/** @enumType */
export type AppTypeWithLiterals = AppType | 'PRODUCT' | 'EVENT' | 'BOOKING';

export interface BookingData {
  /** Booking duration in minutes. */
  durations?: string | null;
}

export interface EventData {
  /** Event schedule. */
  scheduling?: string | null;
  /** Event location. */
  location?: string | null;
}

export interface ButtonStyles {
  /** Text to display on the button. */
  buttonText?: string | null;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /**
   * Text color as a hexadecimal value.
   * @format COLOR_HEX
   */
  textColor?: string | null;
  /**
   * Background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /**
   * Border color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  borderColorHover?: string | null;
  /**
   * Text color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  textColorHover?: string | null;
  /**
   * Background color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  backgroundColorHover?: string | null;
  /** Button size option, one of `SMALL`, `MEDIUM` or `LARGE`. Defaults to `MEDIUM`. */
  buttonSize?: string | null;
}

export interface ImageStyles {
  /** Whether to hide the image. */
  hideImage?: boolean | null;
  /** Position of image. Defaults to `START`. */
  imagePosition?: PositionWithLiterals;
  /** Aspect ratio for the image. Defaults to `SQUARE`. */
  aspectRatio?: AspectRatioWithLiterals;
  /** How the image should be resized. Defaults to `FILL`. */
  resizing?: ResizingWithLiterals;
  /**
   * Image border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /** Image border width in pixels. */
  borderWidth?: number | null;
  /** Image border radius in pixels. */
  borderRadius?: number | null;
}

export interface RibbonStyles {
  /** Text to display on the ribbon. */
  ribbonText?: string | null;
  /**
   * Ribbon background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /**
   * Ribbon text color as a hexadecimal value.
   * @format COLOR_HEX
   */
  textColor?: string | null;
  /**
   * Ribbon border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /** Ribbon border width in pixels. */
  borderWidth?: number | null;
  /** Ribbon border radius in pixels. */
  borderRadius?: number | null;
  /** Placement of the ribbon. Defaults to `IMAGE`. */
  ribbonPlacement?: PlacementWithLiterals;
}

export interface CardStyles {
  /**
   * Card background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /**
   * Card border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /** Card border width in pixels. */
  borderWidth?: number | null;
  /** Card border radius in pixels. */
  borderRadius?: number | null;
  /** Card type. Defaults to `CONTAINED`. */
  type?: CardStylesTypeWithLiterals;
  /** Content alignment. Defaults to `START`. */
  alignment?: AlignmentWithLiterals;
  /** Layout for title and price. Defaults to `STACKED`. */
  titlePriceLayout?: LayoutWithLiterals;
  /**
   * Title text color as a hexadecimal value.
   * @format COLOR_HEX
   */
  titleColor?: string | null;
  /**
   * Text color as a hexadecimal value.
   * @format COLOR_HEX
   */
  textColor?: string | null;
}

export interface PricingData {
  /**
   * Minimum numeric price value as string (e.g., "10.99").
   * @decimalValue options { maxScale:2 }
   */
  valueFrom?: string | null;
  /**
   * Maximum numeric price value as string (e.g., "19.99").
   * @decimalValue options { maxScale:2 }
   */
  valueTo?: string | null;
  /**
   * Numeric price value as string after discount application (e.g., "15.99").
   * @decimalValue options { maxScale:2 }
   */
  discountedValue?: string | null;
  /**
   * Currency of the value in ISO 4217 format (e.g., "USD", "EUR").
   * @format CURRENCY
   */
  currency?: string | null;
  /**
   * Pricing plan ID.
   * @format GUID
   */
  pricingPlanId?: string | null;
}

export interface VideoData {
  /** Styling for the video's container. */
  containerData?: PluginContainerData;
  /** Video details. */
  video?: Media;
  /** Video thumbnail details. */
  thumbnail?: Media;
  /** Sets whether the video's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Video title. */
  title?: string | null;
  /** Video options. */
  options?: PlaybackOptions;
}

export interface PlaybackOptions {
  /** Sets whether the media will automatically start playing. */
  autoPlay?: boolean | null;
  /** Sets whether media's will be looped. */
  playInLoop?: boolean | null;
  /** Sets whether media's controls will be shown. */
  showControls?: boolean | null;
}

export interface EmbedData {
  /** Styling for the oEmbed node's container. */
  containerData?: PluginContainerData;
  /** An [oEmbed](https://www.oembed.com) object. */
  oembed?: Oembed;
  /** Origin asset source. */
  src?: string | null;
}

export interface Oembed {
  /** The resource type. */
  type?: string | null;
  /** The width of the resource specified in the `url` property in pixels. */
  width?: number | null;
  /** The height of the resource specified in the `url` property in pixels. */
  height?: number | null;
  /** Resource title. */
  title?: string | null;
  /** The source URL for the resource. */
  url?: string | null;
  /** HTML for embedding a video player. The HTML should have no padding or margins. */
  html?: string | null;
  /** The name of the author or owner of the resource. */
  authorName?: string | null;
  /** The URL for the author or owner of the resource. */
  authorUrl?: string | null;
  /** The name of the resource provider. */
  providerName?: string | null;
  /** The URL for the resource provider. */
  providerUrl?: string | null;
  /** The URL for a thumbnail image for the resource. If this property is defined, `thumbnailWidth` and `thumbnailHeight` must also be defined. */
  thumbnailUrl?: string | null;
  /** The width of the resource's thumbnail image. If this property is defined, `thumbnailUrl` and `thumbnailHeight` must also be defined. */
  thumbnailWidth?: string | null;
  /** The height of the resource's thumbnail image. If this property is defined, `thumbnailUrl` and `thumbnailWidth`must also be defined. */
  thumbnailHeight?: string | null;
  /** The URL for an embedded viedo. */
  videoUrl?: string | null;
  /** The oEmbed version number.  This value must be `1.0`. */
  version?: string | null;
}

export interface CollapsibleListData {
  /** Styling for the collapsible list's container. */
  containerData?: PluginContainerData;
  /** If `true`, only one item can be expanded at a time. Defaults to `false`. */
  expandOnlyOne?: boolean | null;
  /** Sets which items are expanded when the page loads. */
  initialExpandedItems?: InitialExpandedItemsWithLiterals;
  /** The direction of the text in the list. Either left-to-right or right-to-left. */
  direction?: DirectionWithLiterals;
  /** If `true`, The collapsible item will appear in search results as an FAQ. */
  isQapageData?: boolean | null;
}

export enum InitialExpandedItems {
  /** First item will be expended initally */
  FIRST = 'FIRST',
  /** All items will expended initally */
  ALL = 'ALL',
  /** All items collapsed initally */
  NONE = 'NONE',
}

/** @enumType */
export type InitialExpandedItemsWithLiterals =
  | InitialExpandedItems
  | 'FIRST'
  | 'ALL'
  | 'NONE';

export enum Direction {
  /** Left-to-right */
  LTR = 'LTR',
  /** Right-to-left */
  RTL = 'RTL',
}

/** @enumType */
export type DirectionWithLiterals = Direction | 'LTR' | 'RTL';

export interface TableData {
  /** Styling for the table's container. */
  containerData?: PluginContainerData;
  /** The table's dimensions. */
  dimensions?: Dimensions;
  /**
   * Deprecated: Use `rowHeader` and `columnHeader` instead.
   * @deprecated
   */
  header?: boolean | null;
  /** Sets whether the table's first row is a header. Defaults to `false`. */
  rowHeader?: boolean | null;
  /** Sets whether the table's first column is a header. Defaults to `false`. */
  columnHeader?: boolean | null;
  /** The spacing between cells in pixels. Defaults to `0`. */
  cellSpacing?: number | null;
  /**
   * Padding in pixels for cells. Follows CSS order: top, right, bottom, left.
   * @maxSize 4
   */
  cellPadding?: number[];
}

export interface Dimensions {
  /** An array representing relative width of each column in relation to the other columns. */
  colsWidthRatio?: number[];
  /** An array representing the height of each row in pixels. */
  rowsHeight?: number[];
  /** An array representing the minimum width of each column in pixels. */
  colsMinWidth?: number[];
}

export interface TableCellData {
  /** Styling for the cell's background color and text alignment. */
  cellStyle?: CellStyle;
  /** The cell's border colors. */
  borderColors?: BorderColors;
  /** Defines how many columns the cell spans. Default: 1. */
  colspan?: number | null;
  /** Defines how many rows the cell spans. Default: 1. */
  rowspan?: number | null;
  /** The cell's border widths. */
  borderWidths?: BorderWidths;
}

export enum VerticalAlignment {
  /** Top alignment */
  TOP = 'TOP',
  /** Middle alignment */
  MIDDLE = 'MIDDLE',
  /** Bottom alignment */
  BOTTOM = 'BOTTOM',
}

/** @enumType */
export type VerticalAlignmentWithLiterals =
  | VerticalAlignment
  | 'TOP'
  | 'MIDDLE'
  | 'BOTTOM';

export interface CellStyle {
  /** Vertical alignment for the cell's text. */
  verticalAlignment?: VerticalAlignmentWithLiterals;
  /**
   * Cell background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
}

export interface BorderColors {
  /**
   * Left border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  left?: string | null;
  /**
   * Right border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  right?: string | null;
  /**
   * Top border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  top?: string | null;
  /**
   * Bottom border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  bottom?: string | null;
}

export interface BorderWidths {
  /** Left border width in pixels. */
  left?: number | null;
  /** Right border width in pixels. */
  right?: number | null;
  /** Top border width in pixels. */
  top?: number | null;
  /** Bottom border width in pixels. */
  bottom?: number | null;
}

/**
 * `NullValue` is a singleton enumeration to represent the null value for the
 * `Value` type union.
 *
 * The JSON representation for `NullValue` is JSON `null`.
 */
export enum NullValue {
  /** Null value. */
  NULL_VALUE = 'NULL_VALUE',
}

/** @enumType */
export type NullValueWithLiterals = NullValue | 'NULL_VALUE';

/**
 * `ListValue` is a wrapper around a repeated field of values.
 *
 * The JSON representation for `ListValue` is JSON array.
 */
export interface ListValue {
  /** Repeated field of dynamically typed values. */
  values?: any[];
}

export interface AudioData {
  /** Styling for the audio node's container. */
  containerData?: PluginContainerData;
  /** Audio file details. */
  audio?: Media;
  /** Sets whether the audio node's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Cover image. */
  coverImage?: Media;
  /** Track name. */
  name?: string | null;
  /** Author name. */
  authorName?: string | null;
  /** An HTML version of the audio node. */
  html?: string | null;
}

export interface OrderedListData {
  /** Indentation level from 0-4. */
  indentation?: number;
  /** Offset level from 0-4. */
  offset?: number | null;
  /** List start number. */
  start?: number | null;
}

export interface BulletedListData {
  /** Indentation level from 0-4. */
  indentation?: number;
  /** Offset level from 0-4. */
  offset?: number | null;
}

export interface BlockquoteData {
  /** Indentation level from 1-4. */
  indentation?: number;
}

export interface CaptionData {
  textStyle?: TextStyle;
}

export interface LayoutData {
  /**
   * Background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /** Background image. */
  backgroundImage?: BackgroundImage;
  /**
   * Border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /** Border */
  borderRadius?: number | null;
  /**
   * Backdrop color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backdropColor?: string | null;
  /** Backdrop image.radius in pixels. */
  backdropImage?: BackgroundImage;
  /** Backdrop top padding. */
  backdropPaddingTop?: number | null;
  /** Backdrop bottom padding */
  backdropPaddingBottom?: number | null;
  /** Horizontal and vertical gap between columns */
  gap?: number | null;
  /**
   * Padding in pixels for cells. Follows CSS order: top, right, bottom, left
   * @maxSize 4
   */
  cellPadding?: number[];
  /** Vertical alignment for the cell's items. */
  cellVerticalAlignment?: VerticalAlignmentAlignmentWithLiterals;
  /** Responsiveness behaviour of columns when responsiveness applies. Either stacks or wrappers. */
  responsivenessBehaviour?: ResponsivenessBehaviourWithLiterals;
  /** Size in pixels when responsiveness_behaviour applies */
  responsivenessBreakpoint?: number | null;
  /** Styling for the layout's container. */
  containerData?: PluginContainerData;
  /** Defines where selected design propertied applies to */
  designTarget?: DesignTargetWithLiterals;
  /** Banner configuration. When present, this layout is displayed as a banner. */
  banner?: Banner;
}

export enum Scaling {
  /** Auto image scaling */
  AUTO = 'AUTO',
  /** Contain image scaling */
  CONTAIN = 'CONTAIN',
  /** Cover image scaling */
  COVER = 'COVER',
}

/** @enumType */
export type ScalingWithLiterals = Scaling | 'AUTO' | 'CONTAIN' | 'COVER';

export enum ImagePosition {
  /** Image positioned at the center */
  CENTER = 'CENTER',
  /** Image positioned on the left */
  CENTER_LEFT = 'CENTER_LEFT',
  /** Image positioned on the right */
  CENTER_RIGHT = 'CENTER_RIGHT',
  /** Image positioned at the center top */
  TOP = 'TOP',
  /** Image positioned at the top left */
  TOP_LEFT = 'TOP_LEFT',
  /** Image positioned at the top right */
  TOP_RIGHT = 'TOP_RIGHT',
  /** Image positioned at the center bottom */
  BOTTOM = 'BOTTOM',
  /** Image positioned at the bottom left */
  BOTTOM_LEFT = 'BOTTOM_LEFT',
  /** Image positioned at the bottom right */
  BOTTOM_RIGHT = 'BOTTOM_RIGHT',
}

/** @enumType */
export type ImagePositionWithLiterals =
  | ImagePosition
  | 'CENTER'
  | 'CENTER_LEFT'
  | 'CENTER_RIGHT'
  | 'TOP'
  | 'TOP_LEFT'
  | 'TOP_RIGHT'
  | 'BOTTOM'
  | 'BOTTOM_LEFT'
  | 'BOTTOM_RIGHT';

export enum Origin {
  /** Banner originated from an image */
  IMAGE = 'IMAGE',
  /** Banner originated from a layout */
  LAYOUT = 'LAYOUT',
}

/** @enumType */
export type OriginWithLiterals = Origin | 'IMAGE' | 'LAYOUT';

export interface BackgroundImage {
  /** Background image. */
  media?: Media;
  /** Background image opacity. */
  opacity?: number | null;
  /** Background image scaling. */
  scaling?: ScalingWithLiterals;
  /** Position of background. Defaults to `CENTER`. */
  position?: ImagePositionWithLiterals;
}

export enum VerticalAlignmentAlignment {
  /** Top alignment */
  TOP = 'TOP',
  /** Middle alignment */
  MIDDLE = 'MIDDLE',
  /** Bottom alignment */
  BOTTOM = 'BOTTOM',
}

/** @enumType */
export type VerticalAlignmentAlignmentWithLiterals =
  | VerticalAlignmentAlignment
  | 'TOP'
  | 'MIDDLE'
  | 'BOTTOM';

export enum ResponsivenessBehaviour {
  /** Stacking of columns */
  STACK = 'STACK',
  /** Wrapping of columns */
  WRAP = 'WRAP',
}

/** @enumType */
export type ResponsivenessBehaviourWithLiterals =
  | ResponsivenessBehaviour
  | 'STACK'
  | 'WRAP';

export enum DesignTarget {
  /** Design applied to layout */
  LAYOUT = 'LAYOUT',
  /** Design applied to cells */
  CELL = 'CELL',
}

/** @enumType */
export type DesignTargetWithLiterals = DesignTarget | 'LAYOUT' | 'CELL';

export interface Banner {
  /** Origin of the banner */
  origin?: OriginWithLiterals;
}

export interface LayoutCellData {
  /** Size of the cell in 12 columns grid. */
  colSpan?: number | null;
}

export interface ShapeData {
  /** Styling for the shape's container. */
  containerData?: PluginContainerData;
  /** Shape file details. */
  shape?: Media;
  /** Styling for the shape. */
  styles?: ShapeDataStyles;
}

export interface ShapeDataStyles {
  /**
   * Shape fill color as a hexadecimal value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Map of original color keys to their new color values. */
  colors?: Record<string, string>;
}

export interface Metadata {
  /** Schema version. */
  version?: number;
  /**
   * When the object was created.
   * @readonly
   * @deprecated
   */
  createdTimestamp?: Date | null;
  /**
   * When the object was most recently updated.
   * @deprecated
   */
  updatedTimestamp?: Date | null;
  /** Object ID. */
  id?: string | null;
}

export interface DocumentStyle {
  /** Styling for H1 nodes. */
  headerOne?: TextNodeStyle;
  /** Styling for H2 nodes. */
  headerTwo?: TextNodeStyle;
  /** Styling for H3 nodes. */
  headerThree?: TextNodeStyle;
  /** Styling for H4 nodes. */
  headerFour?: TextNodeStyle;
  /** Styling for H5 nodes. */
  headerFive?: TextNodeStyle;
  /** Styling for H6 nodes. */
  headerSix?: TextNodeStyle;
  /** Styling for paragraph nodes. */
  paragraph?: TextNodeStyle;
  /** Styling for block quote nodes. */
  blockquote?: TextNodeStyle;
  /** Styling for code block nodes. */
  codeBlock?: TextNodeStyle;
}

export interface TextNodeStyle {
  /** The decorations to apply to the node. */
  decorations?: Decoration[];
  /** Padding and background color for the node. */
  nodeStyle?: NodeStyle;
  /** Line height for text in the node. */
  lineHeight?: string | null;
}

export interface Badge {
  /** Badge type. */
  type?: TypeWithLiterals;
  /**
   * Badge text.
   * @maxLength 50
   */
  text?: string | null;
}

export enum Type {
  /** Unknown badge type. */
  UNKNOWN_BADGE_TYPE = 'UNKNOWN_BADGE_TYPE',
  /** 1st priority badge type. */
  FIRST_PRIORITY = 'FIRST_PRIORITY',
  /** 2nd priority badge type. */
  SECOND_PRIORITY = 'SECOND_PRIORITY',
  /** 3rd priority badge type. */
  THIRD_PRIORITY = 'THIRD_PRIORITY',
}

/** @enumType */
export type TypeWithLiterals =
  | Type
  | 'UNKNOWN_BADGE_TYPE'
  | 'FIRST_PRIORITY'
  | 'SECOND_PRIORITY'
  | 'THIRD_PRIORITY';

export interface Empty {}

export interface EventDeleted {
  /** Event deleted timestamp in ISO UTC format. */
  timestamp?: Date | null;
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string;
  /** Event title. */
  title?: string;
  /**
   * Event creator user ID.
   * @format GUID
   */
  userId?: string | null;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type CreateRsvpApplicationErrors =
  | {
      code?: 'EVENT_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVALID_FORM_RESPONSE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'UNEXPECTED_RSVP_STATUS';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVALID_EVENT_TYPE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'MEMBER_ALREADY_REGISTERED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'RSVP_LIMIT_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'GUEST_LIMIT_EXCEEDED';
      description?: string;
      data?: GuestLimitExceededData;
    }
  | {
      code?: 'RSVPS_CLOSED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'RSVPS_NOT_STARTED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'WAITING_LIST_UNAVAILABLE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'MEMBER_EMAIL_ALREADY_REGISTERED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'ADDITIONAL_GUESTS_MUST_HAVE_NAMES';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'FORM_SUBMISSIONS_NOT_SUPPORTED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type UpdateRsvpApplicationErrors =
  | {
      code?: 'INVALID_RSVP_STATUS';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'UNEXPECTED_RSVP_STATUS';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVALID_FORM_RESPONSE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'GUEST_LIMIT_EXCEEDED';
      description?: string;
      data?: GuestLimitExceededData;
    }
  | {
      code?: 'RSVPS_CLOSED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'RSVPS_NOT_STARTED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'WAITING_LIST_UNAVAILABLE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'ADDITIONAL_GUESTS_MUST_HAVE_NAMES';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type BulkUpdateRsvpsApplicationErrors =
  | {
      code?: 'RSVP_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'EVENT_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type BulkDeleteRsvpsByFilterApplicationErrors = {
  code?: 'EMPTY_FILTER';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type CheckInRsvpGuestsApplicationErrors =
  | {
      code?: 'GUEST_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'GUEST_ALREADY_CHECKED_IN';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type CancelRsvpGuestsCheckInApplicationErrors =
  | {
      code?: 'GUEST_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'GUEST_NOT_CHECKED_IN';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type ListCurrentRsvpsApplicationErrors = {
  code?: 'MEMBER_IDENTITY_NOT_FOUND';
  description?: string;
  data?: Record<string, any>;
};
